import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Switch,
    Select,
    MenuItem,
    Menu,
    TextField,
    Checkbox,
    Tooltip,
    Paper,
    Grid,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Divider,
    Chip,
    OutlinedInput,
    ListSubheader,
    IconButton,
} from '@mui/material';
import { LightbulbOutlined, InfoOutlined, SmartToyOutlined, WarningAmberOutlined, AddCircleOutlineOutlined, CloseOutlined } from '@mui/icons-material';

import { AddButton } from '../button/button';

type Schedule = 'daily' | 'manual' | 'on_image_upload' | 'on_task_submission';
type FullPassAction = 'send_to_next' | 'mark_accepted';
type ValueType = 'text' | 'number' | 'date' | 'multiselect' | 'days_select';

interface DeviceMakeOption {
    id: string;
    name: string;
}

interface DeviceModelOption {
    id: string;
    name: string;
    make: string;
}

const DEVICE_MAKE_MODEL_CATALOG: Record<string, string[]> = {
    Apple: [
        'iPhone 6', 'iPhone 6 Black', 'iPhone 6 Blue', 'iPhone 6 256GB',
        'iPhone 6 Plus', 'iPhone 6 Plus Black', 'iPhone 6 Plus Blue', 'iPhone 6 Plus 256GB',
        'iPhone 6s', 'iPhone 6s Black', 'iPhone 6s Blue', 'iPhone 6s 256GB',
        'iPhone 6s Plus', 'iPhone 6s Plus Black', 'iPhone 6s Plus Blue', 'iPhone 6s Plus 256GB',
        'iPhone 7', 'iPhone 7 Black', 'iPhone 7 Blue', 'iPhone 7 256GB',
        'iPhone 7 Plus', 'iPhone 7 Plus Black', 'iPhone 7 Plus Blue', 'iPhone 7 Plus 256GB',
        'iPhone 8', 'iPhone 8 Black', 'iPhone 8 Blue', 'iPhone 8 256GB',
        'iPhone 8 Plus', 'iPhone 8 Plus Black', 'iPhone 8 Plus Blue', 'iPhone 8 Plus 256GB',
        'iPhone X', 'iPhone X Black', 'iPhone X Blue', 'iPhone X 256GB',
        'iPhone XR', 'iPhone XR Black', 'iPhone XR Blue', 'iPhone XR 256GB',
        'iPhone XS', 'iPhone XS Black', 'iPhone XS Blue', 'iPhone XS 256GB',
        'iPhone XS Max', 'iPhone XS Max Black', 'iPhone XS Max Blue', 'iPhone XS Max 256GB',
        'iPhone 11', 'iPhone 11 Black', 'iPhone 11 Blue', 'iPhone 11 256GB',
        'iPhone 11 Pro', 'iPhone 11 Pro Black', 'iPhone 11 Pro Blue', 'iPhone 11 Pro 256GB',
        'iPhone 11 Pro Max', 'iPhone 11 Pro Max Black', 'iPhone 11 Pro Max Blue', 'iPhone 11 Pro Max 256GB',
        'iPhone 12', 'iPhone 12 Black', 'iPhone 12 Blue', 'iPhone 12 256GB',
        'iPhone 12 Pro', 'iPhone 12 Pro Black', 'iPhone 12 Pro Blue', 'iPhone 12 Pro 256GB',
        'iPhone 12 Pro Max', 'iPhone 12 Pro Max Black', 'iPhone 12 Pro Max Blue', 'iPhone 12 Pro Max 256GB',
        'iPhone 13', 'iPhone 13 Black', 'iPhone 13 Blue', 'iPhone 13 256GB',
        'iPhone 13 Pro', 'iPhone 13 Pro Black', 'iPhone 13 Pro Blue', 'iPhone 13 Pro 256GB',
        'iPhone 13 Pro Max', 'iPhone 13 Pro Max Black', 'iPhone 13 Pro Max Blue', 'iPhone 13 Pro Max 256GB',
        'iPhone 14', 'iPhone 14 Black', 'iPhone 14 Blue', 'iPhone 14 256GB',
        'iPhone 14 Pro', 'iPhone 14 Pro Black', 'iPhone 14 Pro Blue', 'iPhone 14 Pro 256GB',
        'iPhone 15', 'iPhone 15 Black', 'iPhone 15 Blue', 'iPhone 15 256GB',
        'iPhone 15 Pro', 'iPhone 15 Pro Black', 'iPhone 15 Pro Blue', 'iPhone 15 Pro 256GB',
    ],
    Samsung: [
        'Galaxy S8', 'Galaxy S8 Black', 'Galaxy S8 Blue', 'Galaxy S8 256GB',
        'Galaxy S9', 'Galaxy S9 Black', 'Galaxy S9 Blue', 'Galaxy S9 256GB',
        'Galaxy S10', 'Galaxy S10 Black', 'Galaxy S10 Blue', 'Galaxy S10 256GB',
        'Galaxy S10+', 'Galaxy S10+ Black', 'Galaxy S10+ Blue', 'Galaxy S10+ 256GB',
        'Galaxy S20', 'Galaxy S20 Black', 'Galaxy S20 Blue', 'Galaxy S20 256GB',
        'Galaxy S20+', 'Galaxy S20+ Black', 'Galaxy S20+ Blue', 'Galaxy S20+ 256GB',
        'Galaxy S20 Ultra', 'Galaxy S20 Ultra Black', 'Galaxy S20 Ultra Blue', 'Galaxy S20 Ultra 256GB',
        'Galaxy S21', 'Galaxy S21 Black', 'Galaxy S21 Blue', 'Galaxy S21 256GB',
        'Galaxy S21+', 'Galaxy S21+ Black', 'Galaxy S21+ Blue', 'Galaxy S21+ 256GB',
        'Galaxy S21 Ultra', 'Galaxy S21 Ultra Black', 'Galaxy S21 Ultra Blue', 'Galaxy S21 Ultra 256GB',
        'Galaxy S22', 'Galaxy S22 Black', 'Galaxy S22 Blue', 'Galaxy S22 256GB',
        'Galaxy S22+', 'Galaxy S22+ Black', 'Galaxy S22+ Blue', 'Galaxy S22+ 256GB',
        'Galaxy S22 Ultra', 'Galaxy S22 Ultra Black', 'Galaxy S22 Ultra Blue', 'Galaxy S22 Ultra 256GB',
        'Galaxy S23', 'Galaxy S23 Black', 'Galaxy S23 Blue', 'Galaxy S23 256GB',
        'Galaxy S23+', 'Galaxy S23+ Black', 'Galaxy S23+ Blue', 'Galaxy S23+ 256GB',
        'Galaxy S23 Ultra', 'Galaxy S23 Ultra Black', 'Galaxy S23 Ultra Blue', 'Galaxy S23 Ultra 256GB',
        'Galaxy S24', 'Galaxy S24 Black', 'Galaxy S24 Blue', 'Galaxy S24 256GB',
        'Galaxy S24+', 'Galaxy S24+ Black', 'Galaxy S24+ Blue', 'Galaxy S24+ 256GB',
        'Galaxy S24 Ultra', 'Galaxy S24 Ultra Black', 'Galaxy S24 Ultra Blue', 'Galaxy S24 Ultra 256GB',
        'Galaxy Note 10', 'Galaxy Note 10 Black', 'Galaxy Note 10 Blue', 'Galaxy Note 10 256GB',
        'Galaxy Note 20', 'Galaxy Note 20 Black', 'Galaxy Note 20 Blue', 'Galaxy Note 20 256GB',
        'Galaxy Note 20 Ultra', 'Galaxy Note 20 Ultra Black', 'Galaxy Note 20 Ultra Blue', 'Galaxy Note 20 Ultra 256GB',
        'Galaxy Z Flip5', 'Galaxy Z Flip5 Black', 'Galaxy Z Flip5 Blue', 'Galaxy Z Flip5 256GB',
        'Galaxy Z Fold5', 'Galaxy Z Fold5 Black', 'Galaxy Z Fold5 Blue', 'Galaxy Z Fold5 256GB',
        'Galaxy A51',
        'Galaxy A71',
        'Galaxy A54', 'Galaxy A54 Black', 'Galaxy A54 Blue', 'Galaxy A54 256GB',
    ],
    Google: [
        'Pixel 3', 'Pixel 3 Black', 'Pixel 3 Blue', 'Pixel 3 256GB',
        'Pixel 3 XL', 'Pixel 3 XL Black', 'Pixel 3 XL Blue', 'Pixel 3 XL 256GB',
        'Pixel 4', 'Pixel 4 Black', 'Pixel 4 Blue', 'Pixel 4 256GB',
        'Pixel 4 XL', 'Pixel 4 XL Black', 'Pixel 4 XL Blue', 'Pixel 4 XL 256GB',
        'Pixel 4a', 'Pixel 4a Black', 'Pixel 4a Blue', 'Pixel 4a 256GB',
        'Pixel 5', 'Pixel 5 Black', 'Pixel 5 Blue', 'Pixel 5 256GB',
        'Pixel 5a', 'Pixel 5a Black', 'Pixel 5a Blue', 'Pixel 5a 256GB',
        'Pixel 6', 'Pixel 6 Black', 'Pixel 6 Blue', 'Pixel 6 256GB',
        'Pixel 6 Pro', 'Pixel 6 Pro Black', 'Pixel 6 Pro Blue', 'Pixel 6 Pro 256GB',
        'Pixel 6a', 'Pixel 6a Black', 'Pixel 6a Blue', 'Pixel 6a 256GB',
        'Pixel 7', 'Pixel 7 Black', 'Pixel 7 Blue', 'Pixel 7 256GB',
        'Pixel 7 Pro', 'Pixel 7 Pro Black', 'Pixel 7 Pro Blue', 'Pixel 7 Pro 256GB',
        'Pixel 7a', 'Pixel 7a Black', 'Pixel 7a Blue', 'Pixel 7a 256GB',
        'Pixel 8', 'Pixel 8 Black', 'Pixel 8 Blue', 'Pixel 8 256GB',
        'Pixel 8 Pro', 'Pixel 8 Pro Black', 'Pixel 8 Pro Blue', 'Pixel 8 Pro 256GB',
        'Pixel 8a', 'Pixel 8a Black', 'Pixel 8a Blue', 'Pixel 8a 256GB',
        'Pixel 9', 'Pixel 9 Black', 'Pixel 9 Blue', 'Pixel 9 256GB',
        'Pixel 9 Pro', 'Pixel 9 Pro Black', 'Pixel 9 Pro Blue', 'Pixel 9 Pro 256GB',
        'Pixel 9 Pro XL', 'Pixel 9 Pro XL Black', 'Pixel 9 Pro XL Blue', 'Pixel 9 Pro XL 256GB',
        'Pixel Fold', 'Pixel Fold Black', 'Pixel Fold Blue', 'Pixel Fold 256GB',
        'Nexus 5', 'Nexus 5 Black', 'Nexus 5 Blue', 'Nexus 5 256GB',
        'Nexus 5X', 'Nexus 5X Black', 'Nexus 5X Blue', 'Nexus 5X 256GB',
        'Nexus 6P', 'Nexus 6P Black', 'Nexus 6P Blue', 'Nexus 6P 256GB',
        'Pixel 2', 'Pixel 2 Black', 'Pixel 2 Blue', 'Pixel 2 256GB',
        'Pixel 2 XL', 'Pixel 2 XL Black', 'Pixel 2 XL Blue', 'Pixel 2 XL 256GB',
    ],
    OnePlus: [
        'OnePlus 3', 'OnePlus 3 Black', 'OnePlus 3 Blue', 'OnePlus 3 256GB',
        'OnePlus 3T', 'OnePlus 3T Black', 'OnePlus 3T Blue', 'OnePlus 3T 256GB',
        'OnePlus 5', 'OnePlus 5 Black', 'OnePlus 5 Blue', 'OnePlus 5 256GB',
        'OnePlus 5T', 'OnePlus 5T Black', 'OnePlus 5T Blue', 'OnePlus 5T 256GB',
        'OnePlus 6', 'OnePlus 6 Black', 'OnePlus 6 Blue', 'OnePlus 6 256GB',
        'OnePlus 6T', 'OnePlus 6T Black', 'OnePlus 6T Blue', 'OnePlus 6T 256GB',
        'OnePlus 7', 'OnePlus 7 Black', 'OnePlus 7 Blue', 'OnePlus 7 256GB',
        'OnePlus 7 Pro', 'OnePlus 7 Pro Black', 'OnePlus 7 Pro Blue', 'OnePlus 7 Pro 256GB',
        'OnePlus 7T', 'OnePlus 7T Black', 'OnePlus 7T Blue', 'OnePlus 7T 256GB',
        'OnePlus 8', 'OnePlus 8 Black', 'OnePlus 8 Blue', 'OnePlus 8 256GB',
        'OnePlus 8 Pro', 'OnePlus 8 Pro Black', 'OnePlus 8 Pro Blue', 'OnePlus 8 Pro 256GB',
        'OnePlus 8T', 'OnePlus 8T Black', 'OnePlus 8T Blue', 'OnePlus 8T 256GB',
        'OnePlus 9', 'OnePlus 9 Black', 'OnePlus 9 Blue', 'OnePlus 9 256GB',
        'OnePlus 9 Pro', 'OnePlus 9 Pro Black', 'OnePlus 9 Pro Blue', 'OnePlus 9 Pro 256GB',
        'OnePlus 9R', 'OnePlus 9R Black', 'OnePlus 9R Blue', 'OnePlus 9R 256GB',
        'OnePlus 10 Pro', 'OnePlus 10 Pro Black', 'OnePlus 10 Pro Blue', 'OnePlus 10 Pro 256GB',
        'OnePlus 10T', 'OnePlus 10T Black', 'OnePlus 10T Blue', 'OnePlus 10T 256GB',
        'OnePlus 11', 'OnePlus 11 Black', 'OnePlus 11 Blue', 'OnePlus 11 256GB',
        'OnePlus 11R', 'OnePlus 11R Black', 'OnePlus 11R Blue', 'OnePlus 11R 256GB',
        'OnePlus 12', 'OnePlus 12 Black', 'OnePlus 12 Blue', 'OnePlus 12 256GB',
        'OnePlus 12R', 'OnePlus 12R Black', 'OnePlus 12R Blue', 'OnePlus 12R 256GB',
        'OnePlus 13', 'OnePlus 13 Black', 'OnePlus 13 Blue', 'OnePlus 13 256GB',
        'OnePlus Nord', 'OnePlus Nord Black', 'OnePlus Nord Blue', 'OnePlus Nord 256GB',
        'OnePlus Nord 2', 'OnePlus Nord 2 Black', 'OnePlus Nord 2 Blue', 'OnePlus Nord 2 256GB',
        'OnePlus Nord 3', 'OnePlus Nord 3 Black', 'OnePlus Nord 3 Blue', 'OnePlus Nord 3 256GB',
        'OnePlus Nord 4', 'OnePlus Nord 4 Black', 'OnePlus Nord 4 Blue', 'OnePlus Nord 4 256GB',
        'OnePlus Open', 'OnePlus Open Black', 'OnePlus Open Blue', 'OnePlus Open 256GB',
    ],
    Xiaomi: [
        'Mi 10', 'Mi 10 Black', 'Mi 10 Blue', 'Mi 10 256GB',
        'Mi 10 Pro', 'Mi 10 Pro Black', 'Mi 10 Pro Blue', 'Mi 10 Pro 256GB',
        'Mi 11', 'Mi 11 Black', 'Mi 11 Blue', 'Mi 11 256GB',
        'Mi 11 Lite', 'Mi 11 Lite Black', 'Mi 11 Lite Blue', 'Mi 11 Lite 256GB',
        'Mi 11 Ultra', 'Mi 11 Ultra Black', 'Mi 11 Ultra Blue', 'Mi 11 Ultra 256GB',
        'Xiaomi 12', 'Xiaomi 12 Black', 'Xiaomi 12 Blue', 'Xiaomi 12 256GB',
        'Xiaomi 12 Pro', 'Xiaomi 12 Pro Black', 'Xiaomi 12 Pro Blue', 'Xiaomi 12 Pro 256GB',
        'Xiaomi 12T', 'Xiaomi 12T Black', 'Xiaomi 12T Blue', 'Xiaomi 12T 256GB',
        'Xiaomi 12T Pro', 'Xiaomi 12T Pro Black', 'Xiaomi 12T Pro Blue', 'Xiaomi 12T Pro 256GB',
        'Xiaomi 13', 'Xiaomi 13 Black', 'Xiaomi 13 Blue', 'Xiaomi 13 256GB',
        'Xiaomi 13 Pro', 'Xiaomi 13 Pro Black', 'Xiaomi 13 Pro Blue', 'Xiaomi 13 Pro 256GB',
        'Xiaomi 13 Ultra', 'Xiaomi 13 Ultra Black', 'Xiaomi 13 Ultra Blue', 'Xiaomi 13 Ultra 256GB',
        'Xiaomi 13T', 'Xiaomi 13T Black', 'Xiaomi 13T Blue', 'Xiaomi 13T 256GB',
        'Xiaomi 13T Pro', 'Xiaomi 13T Pro Black', 'Xiaomi 13T Pro Blue', 'Xiaomi 13T Pro 256GB',
        'Xiaomi 14', 'Xiaomi 14 Black', 'Xiaomi 14 Blue', 'Xiaomi 14 256GB',
        'Xiaomi 14 Pro', 'Xiaomi 14 Pro Black', 'Xiaomi 14 Pro Blue', 'Xiaomi 14 Pro 256GB',
        'Xiaomi 14 Ultra', 'Xiaomi 14 Ultra Black', 'Xiaomi 14 Ultra Blue', 'Xiaomi 14 Ultra 256GB',
        'Xiaomi 15', 'Xiaomi 15 Black', 'Xiaomi 15 Blue', 'Xiaomi 15 256GB',
        'Xiaomi 15 Pro', 'Xiaomi 15 Pro Black', 'Xiaomi 15 Pro Blue', 'Xiaomi 15 Pro 256GB',
        'Xiaomi Civi 2', 'Xiaomi Civi 2 Black', 'Xiaomi Civi 2 Blue', 'Xiaomi Civi 2 256GB',
        'Xiaomi Civi 3', 'Xiaomi Civi 3 Black', 'Xiaomi Civi 3 Blue', 'Xiaomi Civi 3 256GB',
        'Xiaomi Mix Fold 2', 'Xiaomi Mix Fold 2 Black', 'Xiaomi Mix Fold 2 Blue', 'Xiaomi Mix Fold 2 256GB',
        'Xiaomi Mix Fold 3', 'Xiaomi Mix Fold 3 Black', 'Xiaomi Mix Fold 3 Blue', 'Xiaomi Mix Fold 3 256GB',
        'Xiaomi Pad 6', 'Xiaomi Pad 6 Black', 'Xiaomi Pad 6 Blue', 'Xiaomi Pad 6 256GB',
    ],
    OPPO: [
        'Find X2', 'Find X2 Black', 'Find X2 Blue', 'Find X2 256GB',
        'Find X2 Pro', 'Find X2 Pro Black', 'Find X2 Pro Blue', 'Find X2 Pro 256GB',
        'Find X3', 'Find X3 Black', 'Find X3 Blue', 'Find X3 256GB',
        'Find X3 Pro', 'Find X3 Pro Black', 'Find X3 Pro Blue', 'Find X3 Pro 256GB',
        'Find X5', 'Find X5 Black', 'Find X5 Blue', 'Find X5 256GB',
        'Find X5 Pro', 'Find X5 Pro Black', 'Find X5 Pro Blue', 'Find X5 Pro 256GB',
        'Find X6', 'Find X6 Black', 'Find X6 Blue', 'Find X6 256GB',
        'Find X6 Pro', 'Find X6 Pro Black', 'Find X6 Pro Blue', 'Find X6 Pro 256GB',
        'Find X7', 'Find X7 Black', 'Find X7 Blue', 'Find X7 256GB',
        'Find X7 Ultra', 'Find X7 Ultra Black', 'Find X7 Ultra Blue', 'Find X7 Ultra 256GB',
        'Find X8', 'Find X8 Black', 'Find X8 Blue', 'Find X8 256GB',
        'Find X8 Pro', 'Find X8 Pro Black', 'Find X8 Pro Blue', 'Find X8 Pro 256GB',
        'Find N', 'Find N Black', 'Find N Blue', 'Find N 256GB',
        'Find N2', 'Find N2 Black', 'Find N2 Blue', 'Find N2 256GB',
        'Find N3', 'Find N3 Black', 'Find N3 Blue', 'Find N3 256GB',
        'Reno 5', 'Reno 5 Black', 'Reno 5 Blue', 'Reno 5 256GB',
        'Reno 6', 'Reno 6 Black', 'Reno 6 Blue', 'Reno 6 256GB',
        'Reno 7', 'Reno 7 Black', 'Reno 7 Blue', 'Reno 7 256GB',
        'Reno 8', 'Reno 8 Black', 'Reno 8 Blue', 'Reno 8 256GB',
        'Reno 8 Pro', 'Reno 8 Pro Black', 'Reno 8 Pro Blue', 'Reno 8 Pro 256GB',
        'Reno 10', 'Reno 10 Black', 'Reno 10 Blue', 'Reno 10 256GB',
        'Reno 10 Pro', 'Reno 10 Pro Black', 'Reno 10 Pro Blue', 'Reno 10 Pro 256GB',
        'Reno 10 Pro+', 'Reno 10 Pro+ Black', 'Reno 10 Pro+ Blue', 'Reno 10 Pro+ 256GB',
        'Reno 11', 'Reno 11 Black', 'Reno 11 Blue', 'Reno 11 256GB',
        'Reno 11 Pro', 'Reno 11 Pro Black', 'Reno 11 Pro Blue', 'Reno 11 Pro 256GB',
        'Reno 12', 'Reno 12 Black', 'Reno 12 Blue', 'Reno 12 256GB',
        'Reno 12 Pro', 'Reno 12 Pro Black', 'Reno 12 Pro Blue', 'Reno 12 Pro 256GB',
    ],
    Vivo: [
        'Vivo V20', 'Vivo V20 Black', 'Vivo V20 Blue', 'Vivo V20 256GB',
        'Vivo V20 Pro', 'Vivo V20 Pro Black', 'Vivo V20 Pro Blue', 'Vivo V20 Pro 256GB',
        'Vivo V21', 'Vivo V21 Black', 'Vivo V21 Blue', 'Vivo V21 256GB',
        'Vivo V23', 'Vivo V23 Black', 'Vivo V23 Blue', 'Vivo V23 256GB',
        'Vivo V23 Pro', 'Vivo V23 Pro Black', 'Vivo V23 Pro Blue', 'Vivo V23 Pro 256GB',
        'Vivo V25', 'Vivo V25 Black', 'Vivo V25 Blue', 'Vivo V25 256GB',
        'Vivo V25 Pro', 'Vivo V25 Pro Black', 'Vivo V25 Pro Blue', 'Vivo V25 Pro 256GB',
        'Vivo V27', 'Vivo V27 Black', 'Vivo V27 Blue', 'Vivo V27 256GB',
        'Vivo V27 Pro', 'Vivo V27 Pro Black', 'Vivo V27 Pro Blue', 'Vivo V27 Pro 256GB',
        'Vivo V29', 'Vivo V29 Black', 'Vivo V29 Blue', 'Vivo V29 256GB',
        'Vivo V29 Pro', 'Vivo V29 Pro Black', 'Vivo V29 Pro Blue', 'Vivo V29 Pro 256GB',
        'Vivo V30', 'Vivo V30 Black', 'Vivo V30 Blue', 'Vivo V30 256GB',
        'Vivo V30 Pro', 'Vivo V30 Pro Black', 'Vivo V30 Pro Blue', 'Vivo V30 Pro 256GB',
        'Vivo V40', 'Vivo V40 Black', 'Vivo V40 Blue', 'Vivo V40 256GB',
        'Vivo V40 Pro', 'Vivo V40 Pro Black', 'Vivo V40 Pro Blue', 'Vivo V40 Pro 256GB',
        'Vivo Y20', 'Vivo Y20 Black', 'Vivo Y20 Blue', 'Vivo Y20 256GB',
        'Vivo Y21', 'Vivo Y21 Black', 'Vivo Y21 Blue', 'Vivo Y21 256GB',
        'Vivo Y35', 'Vivo Y35 Black', 'Vivo Y35 Blue', 'Vivo Y35 256GB',
        'Vivo Y56', 'Vivo Y56 Black', 'Vivo Y56 Blue', 'Vivo Y56 256GB',
        'Vivo Y100', 'Vivo Y100 Black', 'Vivo Y100 Blue', 'Vivo Y100 256GB',
        'Vivo T1', 'Vivo T1 Black', 'Vivo T1 Blue', 'Vivo T1 256GB',
        'Vivo T2', 'Vivo T2 Black', 'Vivo T2 Blue', 'Vivo T2 256GB',
        'Vivo T3', 'Vivo T3 Black', 'Vivo T3 Blue', 'Vivo T3 256GB',
        'Vivo X90', 'Vivo X90 Black', 'Vivo X90 Blue', 'Vivo X90 256GB',
        'Vivo X100', 'Vivo X100 Black', 'Vivo X100 Blue', 'Vivo X100 256GB',
    ],
    Realme: [
        'Realme 7', 'Realme 7 Black', 'Realme 7 Blue', 'Realme 7 256GB',
        'Realme 7 Pro', 'Realme 7 Pro Black', 'Realme 7 Pro Blue', 'Realme 7 Pro 256GB',
        'Realme 8', 'Realme 8 Black', 'Realme 8 Blue', 'Realme 8 256GB',
        'Realme 8 Pro', 'Realme 8 Pro Black', 'Realme 8 Pro Blue', 'Realme 8 Pro 256GB',
        'Realme 9', 'Realme 9 Black', 'Realme 9 Blue', 'Realme 9 256GB',
        'Realme 9 Pro', 'Realme 9 Pro Black', 'Realme 9 Pro Blue', 'Realme 9 Pro 256GB',
        'Realme 9 Pro+', 'Realme 9 Pro+ Black', 'Realme 9 Pro+ Blue', 'Realme 9 Pro+ 256GB',
        'Realme 10', 'Realme 10 Black', 'Realme 10 Blue', 'Realme 10 256GB',
        'Realme 10 Pro', 'Realme 10 Pro Black', 'Realme 10 Pro Blue', 'Realme 10 Pro 256GB',
        'Realme 10 Pro+', 'Realme 10 Pro+ Black', 'Realme 10 Pro+ Blue', 'Realme 10 Pro+ 256GB',
        'Realme 11', 'Realme 11 Black', 'Realme 11 Blue', 'Realme 11 256GB',
        'Realme 11 Pro', 'Realme 11 Pro Black', 'Realme 11 Pro Blue', 'Realme 11 Pro 256GB',
        'Realme 11 Pro+', 'Realme 11 Pro+ Black', 'Realme 11 Pro+ Blue', 'Realme 11 Pro+ 256GB',
        'Realme 12', 'Realme 12 Black', 'Realme 12 Blue', 'Realme 12 256GB',
        'Realme 12 Pro', 'Realme 12 Pro Black', 'Realme 12 Pro Blue', 'Realme 12 Pro 256GB',
        'Realme 12 Pro+', 'Realme 12 Pro+ Black', 'Realme 12 Pro+ Blue', 'Realme 12 Pro+ 256GB',
        'Realme 13', 'Realme 13 Black', 'Realme 13 Blue', 'Realme 13 256GB',
        'Realme 13 Pro', 'Realme 13 Pro Black', 'Realme 13 Pro Blue', 'Realme 13 Pro 256GB',
        'Realme 13 Pro+', 'Realme 13 Pro+ Black', 'Realme 13 Pro+ Blue', 'Realme 13 Pro+ 256GB',
        'Realme Narzo 50', 'Realme Narzo 50 Black', 'Realme Narzo 50 Blue', 'Realme Narzo 50 256GB',
        'Realme Narzo 60', 'Realme Narzo 60 Black', 'Realme Narzo 60 Blue', 'Realme Narzo 60 256GB',
        'Realme Narzo 70', 'Realme Narzo 70 Black', 'Realme Narzo 70 Blue', 'Realme Narzo 70 256GB',
        'Realme GT 2', 'Realme GT 2 Black', 'Realme GT 2 Blue', 'Realme GT 2 256GB',
        'Realme GT 5', 'Realme GT 5 Black', 'Realme GT 5 Blue', 'Realme GT 5 256GB',
        'Realme GT 6', 'Realme GT 6 Black', 'Realme GT 6 Blue', 'Realme GT 6 256GB',
    ],
    Motorola: [
        'Moto G31', 'Moto G31 Black', 'Moto G31 Blue', 'Moto G31 256GB',
        'Moto G32', 'Moto G32 Black', 'Moto G32 Blue', 'Moto G32 256GB',
        'Moto G42', 'Moto G42 Black', 'Moto G42 Blue', 'Moto G42 256GB',
        'Moto G52', 'Moto G52 Black', 'Moto G52 Blue', 'Moto G52 256GB',
        'Moto G53', 'Moto G53 Black', 'Moto G53 Blue', 'Moto G53 256GB',
        'Moto G54', 'Moto G54 Black', 'Moto G54 Blue', 'Moto G54 256GB',
        'Moto G55', 'Moto G55 Black', 'Moto G55 Blue', 'Moto G55 256GB',
        'Moto G60', 'Moto G60 Black', 'Moto G60 Blue', 'Moto G60 256GB',
        'Moto G62', 'Moto G62 Black', 'Moto G62 Blue', 'Moto G62 256GB',
        'Moto G64', 'Moto G64 Black', 'Moto G64 Blue', 'Moto G64 256GB',
        'Moto G71', 'Moto G71 Black', 'Moto G71 Blue', 'Moto G71 256GB',
        'Moto G72', 'Moto G72 Black', 'Moto G72 Blue', 'Moto G72 256GB',
        'Moto G73', 'Moto G73 Black', 'Moto G73 Blue', 'Moto G73 256GB',
        'Moto G82', 'Moto G82 Black', 'Moto G82 Blue', 'Moto G82 256GB',
        'Moto G84', 'Moto G84 Black', 'Moto G84 Blue', 'Moto G84 256GB',
        'Moto G85', 'Moto G85 Black', 'Moto G85 Blue', 'Moto G85 256GB',
        'Moto Edge 20', 'Moto Edge 20 Black', 'Moto Edge 20 Blue', 'Moto Edge 20 256GB',
        'Moto Edge 30', 'Moto Edge 30 Black', 'Moto Edge 30 Blue', 'Moto Edge 30 256GB',
        'Moto Edge 40', 'Moto Edge 40 Black', 'Moto Edge 40 Blue', 'Moto Edge 40 256GB',
        'Moto Edge 50', 'Moto Edge 50 Black', 'Moto Edge 50 Blue', 'Moto Edge 50 256GB',
        'Moto Edge 50 Pro', 'Moto Edge 50 Pro Black', 'Moto Edge 50 Pro Blue', 'Moto Edge 50 Pro 256GB',
        'Moto Edge 50 Ultra', 'Moto Edge 50 Ultra Black', 'Moto Edge 50 Ultra Blue', 'Moto Edge 50 Ultra 256GB',
        'Moto Razr 40', 'Moto Razr 40 Black', 'Moto Razr 40 Blue', 'Moto Razr 40 256GB',
        'Moto Razr 50', 'Moto Razr 50 Black', 'Moto Razr 50 Blue', 'Moto Razr 50 256GB',
        'Moto G Power (2023)', 'Moto G Power (2023) Black', 'Moto G Power (2023) Blue', 'Moto G Power (2023) 256GB',
    ],
    Nokia: [
        'Nokia 5.4', 'Nokia 5.4 Black', 'Nokia 5.4 Blue', 'Nokia 5.4 256GB',
        'Nokia 6.2', 'Nokia 6.2 Black', 'Nokia 6.2 Blue', 'Nokia 6.2 256GB',
        'Nokia 7.2', 'Nokia 7.2 Black', 'Nokia 7.2 Blue', 'Nokia 7.2 256GB',
        'Nokia 8.3 5G', 'Nokia 8.3 5G Black', 'Nokia 8.3 5G Blue', 'Nokia 8.3 5G 256GB',
        'Nokia 9 PureView', 'Nokia 9 PureView Black', 'Nokia 9 PureView Blue', 'Nokia 9 PureView 256GB',
        'Nokia C10', 'Nokia C10 Black', 'Nokia C10 Blue', 'Nokia C10 256GB',
        'Nokia C12', 'Nokia C12 Black', 'Nokia C12 Blue', 'Nokia C12 256GB',
        'Nokia C20', 'Nokia C20 Black', 'Nokia C20 Blue', 'Nokia C20 256GB',
        'Nokia C21', 'Nokia C21 Black', 'Nokia C21 Blue', 'Nokia C21 256GB',
        'Nokia C22', 'Nokia C22 Black', 'Nokia C22 Blue', 'Nokia C22 256GB',
        'Nokia C31', 'Nokia C31 Black', 'Nokia C31 Blue', 'Nokia C31 256GB',
        'Nokia C32', 'Nokia C32 Black', 'Nokia C32 Blue', 'Nokia C32 256GB',
        'Nokia G10', 'Nokia G10 Black', 'Nokia G10 Blue', 'Nokia G10 256GB',
        'Nokia G11', 'Nokia G11 Black', 'Nokia G11 Blue', 'Nokia G11 256GB',
        'Nokia G20', 'Nokia G20 Black', 'Nokia G20 Blue', 'Nokia G20 256GB',
        'Nokia G21', 'Nokia G21 Black', 'Nokia G21 Blue', 'Nokia G21 256GB',
        'Nokia G22', 'Nokia G22 Black', 'Nokia G22 Blue', 'Nokia G22 256GB',
        'Nokia G42', 'Nokia G42 Black', 'Nokia G42 Blue', 'Nokia G42 256GB',
        'Nokia G50', 'Nokia G50 Black', 'Nokia G50 Blue', 'Nokia G50 256GB',
        'Nokia G60', 'Nokia G60 Black', 'Nokia G60 Blue', 'Nokia G60 256GB',
        'Nokia X10', 'Nokia X10 Black', 'Nokia X10 Blue', 'Nokia X10 256GB',
        'Nokia X20', 'Nokia X20 Black', 'Nokia X20 Blue', 'Nokia X20 256GB',
        'Nokia X30', 'Nokia X30 Black', 'Nokia X30 Blue', 'Nokia X30 256GB',
        'Nokia XR20', 'Nokia XR20 Black', 'Nokia XR20 Blue', 'Nokia XR20 256GB',
        'Nokia 2660 Flip', 'Nokia 2660 Flip Black', 'Nokia 2660 Flip Blue', 'Nokia 2660 Flip 256GB',
    ],
    Huawei: [
        'Huawei P30', 'Huawei P30 Black', 'Huawei P30 Blue', 'Huawei P30 256GB',
        'Huawei P30 Pro', 'Huawei P30 Pro Black', 'Huawei P30 Pro Blue', 'Huawei P30 Pro 256GB',
        'Huawei P40', 'Huawei P40 Black', 'Huawei P40 Blue', 'Huawei P40 256GB',
        'Huawei P40 Pro', 'Huawei P40 Pro Black', 'Huawei P40 Pro Blue', 'Huawei P40 Pro 256GB',
        'Huawei P50', 'Huawei P50 Black', 'Huawei P50 Blue', 'Huawei P50 256GB',
        'Huawei P50 Pro', 'Huawei P50 Pro Black', 'Huawei P50 Pro Blue', 'Huawei P50 Pro 256GB',
        'Huawei P60', 'Huawei P60 Black', 'Huawei P60 Blue', 'Huawei P60 256GB',
        'Huawei P60 Pro', 'Huawei P60 Pro Black', 'Huawei P60 Pro Blue', 'Huawei P60 Pro 256GB',
        'Huawei Mate 20', 'Huawei Mate 20 Black', 'Huawei Mate 20 Blue', 'Huawei Mate 20 256GB',
        'Huawei Mate 20 Pro', 'Huawei Mate 20 Pro Black', 'Huawei Mate 20 Pro Blue', 'Huawei Mate 20 Pro 256GB',
        'Huawei Mate 30', 'Huawei Mate 30 Black', 'Huawei Mate 30 Blue', 'Huawei Mate 30 256GB',
        'Huawei Mate 40', 'Huawei Mate 40 Black', 'Huawei Mate 40 Blue', 'Huawei Mate 40 256GB',
        'Huawei Mate 50', 'Huawei Mate 50 Black', 'Huawei Mate 50 Blue', 'Huawei Mate 50 256GB',
        'Huawei Mate 50 Pro', 'Huawei Mate 50 Pro Black', 'Huawei Mate 50 Pro Blue', 'Huawei Mate 50 Pro 256GB',
        'Huawei Mate 60', 'Huawei Mate 60 Black', 'Huawei Mate 60 Blue', 'Huawei Mate 60 256GB',
        'Huawei Mate 60 Pro', 'Huawei Mate 60 Pro Black', 'Huawei Mate 60 Pro Blue', 'Huawei Mate 60 Pro 256GB',
        'Huawei Nova 7', 'Huawei Nova 7 Black', 'Huawei Nova 7 Blue', 'Huawei Nova 7 256GB',
        'Huawei Nova 8', 'Huawei Nova 8 Black', 'Huawei Nova 8 Blue', 'Huawei Nova 8 256GB',
        'Huawei Nova 9', 'Huawei Nova 9 Black', 'Huawei Nova 9 Blue', 'Huawei Nova 9 256GB',
        'Huawei Nova 10', 'Huawei Nova 10 Black', 'Huawei Nova 10 Blue', 'Huawei Nova 10 256GB',
        'Huawei Nova 11', 'Huawei Nova 11 Black', 'Huawei Nova 11 Blue', 'Huawei Nova 11 256GB',
        'Huawei Y7p', 'Huawei Y7p Black', 'Huawei Y7p Blue', 'Huawei Y7p 256GB',
        'Huawei Y9a', 'Huawei Y9a Black', 'Huawei Y9a Blue', 'Huawei Y9a 256GB',
        'Huawei P50 Pocket', 'Huawei P50 Pocket Black', 'Huawei P50 Pocket Blue', 'Huawei P50 Pocket 256GB',
        'Huawei Mate X3', 'Huawei Mate X3 Black', 'Huawei Mate X3 Blue', 'Huawei Mate X3 256GB',
    ],
    Honor: [
        'Honor 9X', 'Honor 9X Black', 'Honor 9X Blue', 'Honor 9X 256GB',
        'Honor 10', 'Honor 10 Black', 'Honor 10 Blue', 'Honor 10 256GB',
        'Honor 10 Lite', 'Honor 10 Lite Black', 'Honor 10 Lite Blue', 'Honor 10 Lite 256GB',
        'Honor 20', 'Honor 20 Black', 'Honor 20 Blue', 'Honor 20 256GB',
        'Honor 20 Pro', 'Honor 20 Pro Black', 'Honor 20 Pro Blue', 'Honor 20 Pro 256GB',
        'Honor 30', 'Honor 30 Black', 'Honor 30 Blue', 'Honor 30 256GB',
        'Honor 30 Pro', 'Honor 30 Pro Black', 'Honor 30 Pro Blue', 'Honor 30 Pro 256GB',
        'Honor 50', 'Honor 50 Black', 'Honor 50 Blue', 'Honor 50 256GB',
        'Honor 50 Lite', 'Honor 50 Lite Black', 'Honor 50 Lite Blue', 'Honor 50 Lite 256GB',
        'Honor 60', 'Honor 60 Black', 'Honor 60 Blue', 'Honor 60 256GB',
        'Honor 60 Pro', 'Honor 60 Pro Black', 'Honor 60 Pro Blue', 'Honor 60 Pro 256GB',
        'Honor 70', 'Honor 70 Black', 'Honor 70 Blue', 'Honor 70 256GB',
        'Honor 80', 'Honor 80 Black', 'Honor 80 Blue', 'Honor 80 256GB',
        'Honor 80 Pro', 'Honor 80 Pro Black', 'Honor 80 Pro Blue', 'Honor 80 Pro 256GB',
        'Honor 90', 'Honor 90 Black', 'Honor 90 Blue', 'Honor 90 256GB',
        'Honor 90 Lite', 'Honor 90 Lite Black', 'Honor 90 Lite Blue', 'Honor 90 Lite 256GB',
        'Honor 100', 'Honor 100 Black', 'Honor 100 Blue', 'Honor 100 256GB',
        'Honor 100 Pro', 'Honor 100 Pro Black', 'Honor 100 Pro Blue', 'Honor 100 Pro 256GB',
        'Honor 200', 'Honor 200 Black', 'Honor 200 Blue', 'Honor 200 256GB',
        'Honor 200 Pro', 'Honor 200 Pro Black', 'Honor 200 Pro Blue', 'Honor 200 Pro 256GB',
        'Honor X6', 'Honor X6 Black', 'Honor X6 Blue', 'Honor X6 256GB',
        'Honor X7', 'Honor X7 Black', 'Honor X7 Blue', 'Honor X7 256GB',
        'Honor X8', 'Honor X8 Black', 'Honor X8 Blue', 'Honor X8 256GB',
        'Honor X9b', 'Honor X9b Black', 'Honor X9b Blue', 'Honor X9b 256GB',
        'Honor Magic 5 Pro', 'Honor Magic 5 Pro Black', 'Honor Magic 5 Pro Blue', 'Honor Magic 5 Pro 256GB',
    ],
    Sony: [
        'Xperia 1', 'Xperia 1 Black', 'Xperia 1 Blue', 'Xperia 1 256GB',
        'Xperia 1 II', 'Xperia 1 II Black', 'Xperia 1 II Blue', 'Xperia 1 II 256GB',
        'Xperia 1 III', 'Xperia 1 III Black', 'Xperia 1 III Blue', 'Xperia 1 III 256GB',
        'Xperia 1 IV', 'Xperia 1 IV Black', 'Xperia 1 IV Blue', 'Xperia 1 IV 256GB',
        'Xperia 1 V', 'Xperia 1 V Black', 'Xperia 1 V Blue', 'Xperia 1 V 256GB',
        'Xperia 1 VI', 'Xperia 1 VI Black', 'Xperia 1 VI Blue', 'Xperia 1 VI 256GB',
        'Xperia 5', 'Xperia 5 Black', 'Xperia 5 Blue', 'Xperia 5 256GB',
        'Xperia 5 II', 'Xperia 5 II Black', 'Xperia 5 II Blue', 'Xperia 5 II 256GB',
        'Xperia 5 III', 'Xperia 5 III Black', 'Xperia 5 III Blue', 'Xperia 5 III 256GB',
        'Xperia 5 IV', 'Xperia 5 IV Black', 'Xperia 5 IV Blue', 'Xperia 5 IV 256GB',
        'Xperia 5 V', 'Xperia 5 V Black', 'Xperia 5 V Blue', 'Xperia 5 V 256GB',
        'Xperia 10 II', 'Xperia 10 II Black', 'Xperia 10 II Blue', 'Xperia 10 II 256GB',
        'Xperia 10 III', 'Xperia 10 III Black', 'Xperia 10 III Blue', 'Xperia 10 III 256GB',
        'Xperia 10 IV', 'Xperia 10 IV Black', 'Xperia 10 IV Blue', 'Xperia 10 IV 256GB',
        'Xperia 10 V', 'Xperia 10 V Black', 'Xperia 10 V Blue', 'Xperia 10 V 256GB',
        'Xperia 10 VI', 'Xperia 10 VI Black', 'Xperia 10 VI Blue', 'Xperia 10 VI 256GB',
        'Xperia Ace', 'Xperia Ace Black', 'Xperia Ace Blue', 'Xperia Ace 256GB',
        'Xperia Ace II', 'Xperia Ace II Black', 'Xperia Ace II Blue', 'Xperia Ace II 256GB',
        'Xperia Ace III', 'Xperia Ace III Black', 'Xperia Ace III Blue', 'Xperia Ace III 256GB',
        'Xperia Pro', 'Xperia Pro Black', 'Xperia Pro Blue', 'Xperia Pro 256GB',
        'Xperia Pro-I', 'Xperia Pro-I Black', 'Xperia Pro-I Blue', 'Xperia Pro-I 256GB',
        'Xperia XZ1', 'Xperia XZ1 Black', 'Xperia XZ1 Blue', 'Xperia XZ1 256GB',
        'Xperia XZ2', 'Xperia XZ2 Black', 'Xperia XZ2 Blue', 'Xperia XZ2 256GB',
        'Xperia XZ3', 'Xperia XZ3 Black', 'Xperia XZ3 Blue', 'Xperia XZ3 256GB',
        'Xperia XZ Premium', 'Xperia XZ Premium Black', 'Xperia XZ Premium Blue', 'Xperia XZ Premium 256GB',
    ],
    Asus: [
        'Zenfone 5', 'Zenfone 5 Black', 'Zenfone 5 Blue', 'Zenfone 5 256GB',
        'Zenfone 5Z', 'Zenfone 5Z Black', 'Zenfone 5Z Blue', 'Zenfone 5Z 256GB',
        'Zenfone 6', 'Zenfone 6 Black', 'Zenfone 6 Blue', 'Zenfone 6 256GB',
        'Zenfone 7', 'Zenfone 7 Black', 'Zenfone 7 Blue', 'Zenfone 7 256GB',
        'Zenfone 7 Pro', 'Zenfone 7 Pro Black', 'Zenfone 7 Pro Blue', 'Zenfone 7 Pro 256GB',
        'Zenfone 8', 'Zenfone 8 Black', 'Zenfone 8 Blue', 'Zenfone 8 256GB',
        'Zenfone 8 Flip', 'Zenfone 8 Flip Black', 'Zenfone 8 Flip Blue', 'Zenfone 8 Flip 256GB',
        'Zenfone 9', 'Zenfone 9 Black', 'Zenfone 9 Blue', 'Zenfone 9 256GB',
        'Zenfone 10', 'Zenfone 10 Black', 'Zenfone 10 Blue', 'Zenfone 10 256GB',
        'Zenfone 11 Ultra', 'Zenfone 11 Ultra Black', 'Zenfone 11 Ultra Blue', 'Zenfone 11 Ultra 256GB',
        'ROG Phone', 'ROG Phone Black', 'ROG Phone Blue', 'ROG Phone 256GB',
        'ROG Phone II', 'ROG Phone II Black', 'ROG Phone II Blue', 'ROG Phone II 256GB',
        'ROG Phone 3', 'ROG Phone 3 Black', 'ROG Phone 3 Blue', 'ROG Phone 3 256GB',
        'ROG Phone 5', 'ROG Phone 5 Black', 'ROG Phone 5 Blue', 'ROG Phone 5 256GB',
        'ROG Phone 5s', 'ROG Phone 5s Black', 'ROG Phone 5s Blue', 'ROG Phone 5s 256GB',
        'ROG Phone 6', 'ROG Phone 6 Black', 'ROG Phone 6 Blue', 'ROG Phone 6 256GB',
        'ROG Phone 7', 'ROG Phone 7 Black', 'ROG Phone 7 Blue', 'ROG Phone 7 256GB',
        'ROG Phone 8', 'ROG Phone 8 Black', 'ROG Phone 8 Blue', 'ROG Phone 8 256GB',
        'ROG Phone 9', 'ROG Phone 9 Black', 'ROG Phone 9 Blue', 'ROG Phone 9 256GB',
        'Asus 6z', 'Asus 6z Black', 'Asus 6z Blue', 'Asus 6z 256GB',
        'Asus 8z', 'Asus 8z Black', 'Asus 8z Blue', 'Asus 8z 256GB',
        'Zenfone Max Pro M1', 'Zenfone Max Pro M1 Black', 'Zenfone Max Pro M1 Blue', 'Zenfone Max Pro M1 256GB',
        'Zenfone Max Pro M2', 'Zenfone Max Pro M2 Black', 'Zenfone Max Pro M2 Blue', 'Zenfone Max Pro M2 256GB',
        'Zenfone Live', 'Zenfone Live Black', 'Zenfone Live Blue', 'Zenfone Live 256GB',
        'Zenfone Zoom', 'Zenfone Zoom Black', 'Zenfone Zoom Blue', 'Zenfone Zoom 256GB',
    ],
    Redmi: [
        'Redmi 8', 'Redmi 8 Black', 'Redmi 8 Blue', 'Redmi 8 256GB',
        'Redmi 8A', 'Redmi 8A Black', 'Redmi 8A Blue', 'Redmi 8A 256GB',
        'Redmi 9', 'Redmi 9 Black', 'Redmi 9 Blue', 'Redmi 9 256GB',
        'Redmi 9A', 'Redmi 9A Black', 'Redmi 9A Blue', 'Redmi 9A 256GB',
        'Redmi 9C', 'Redmi 9C Black', 'Redmi 9C Blue', 'Redmi 9C 256GB',
        'Redmi 9 Prime', 'Redmi 9 Prime Black', 'Redmi 9 Prime Blue', 'Redmi 9 Prime 256GB',
        'Redmi 10', 'Redmi 10 Black', 'Redmi 10 Blue', 'Redmi 10 256GB',
        'Redmi 10 Prime', 'Redmi 10 Prime Black', 'Redmi 10 Prime Blue', 'Redmi 10 Prime 256GB',
        'Redmi 10A', 'Redmi 10A Black', 'Redmi 10A Blue', 'Redmi 10A 256GB',
        'Redmi 11 Prime', 'Redmi 11 Prime Black', 'Redmi 11 Prime Blue', 'Redmi 11 Prime 256GB',
        'Redmi 12', 'Redmi 12 Black', 'Redmi 12 Blue', 'Redmi 12 256GB',
        'Redmi 12 5G', 'Redmi 12 5G Black', 'Redmi 12 5G Blue', 'Redmi 12 5G 256GB',
        'Redmi 13', 'Redmi 13 Black', 'Redmi 13 Blue', 'Redmi 13 256GB',
        'Redmi 13 5G', 'Redmi 13 5G Black', 'Redmi 13 5G Blue', 'Redmi 13 5G 256GB',
        'Redmi 13C', 'Redmi 13C Black', 'Redmi 13C Blue', 'Redmi 13C 256GB',
        'Redmi 14C', 'Redmi 14C Black', 'Redmi 14C Blue', 'Redmi 14C 256GB',
        'Redmi Note 8', 'Redmi Note 8 Black', 'Redmi Note 8 Blue', 'Redmi Note 8 256GB',
        'Redmi Note 9', 'Redmi Note 9 Black', 'Redmi Note 9 Blue', 'Redmi Note 9 256GB',
        'Redmi Note 10', 'Redmi Note 10 Black', 'Redmi Note 10 Blue', 'Redmi Note 10 256GB',
        'Redmi Note 10 Pro', 'Redmi Note 10 Pro Black', 'Redmi Note 10 Pro Blue', 'Redmi Note 10 Pro 256GB',
        'Redmi Note 11', 'Redmi Note 11 Black', 'Redmi Note 11 Blue', 'Redmi Note 11 256GB',
        'Redmi Note 11 Pro', 'Redmi Note 11 Pro Black', 'Redmi Note 11 Pro Blue', 'Redmi Note 11 Pro 256GB',
        'Redmi Note 12', 'Redmi Note 12 Black', 'Redmi Note 12 Blue', 'Redmi Note 12 256GB',
        'Redmi Note 13', 'Redmi Note 13 Black', 'Redmi Note 13 Blue', 'Redmi Note 13 256GB',
        'Redmi Note 14 Pro', 'Redmi Note 14 Pro Black', 'Redmi Note 14 Pro Blue', 'Redmi Note 14 Pro 256GB',
    ],
    POCO: [
        'POCO F1', 'POCO F1 Black', 'POCO F1 Blue', 'POCO F1 256GB',
        'POCO F2 Pro', 'POCO F2 Pro Black', 'POCO F2 Pro Blue', 'POCO F2 Pro 256GB',
        'POCO F3', 'POCO F3 Black', 'POCO F3 Blue', 'POCO F3 256GB',
        'POCO F4', 'POCO F4 Black', 'POCO F4 Blue', 'POCO F4 256GB',
        'POCO F4 GT', 'POCO F4 GT Black', 'POCO F4 GT Blue', 'POCO F4 GT 256GB',
        'POCO F5', 'POCO F5 Black', 'POCO F5 Blue', 'POCO F5 256GB',
        'POCO F5 Pro', 'POCO F5 Pro Black', 'POCO F5 Pro Blue', 'POCO F5 Pro 256GB',
        'POCO F6', 'POCO F6 Black', 'POCO F6 Blue', 'POCO F6 256GB',
        'POCO F6 Pro', 'POCO F6 Pro Black', 'POCO F6 Pro Blue', 'POCO F6 Pro 256GB',
        'POCO F7', 'POCO F7 Black', 'POCO F7 Blue', 'POCO F7 256GB',
        'POCO X2', 'POCO X2 Black', 'POCO X2 Blue', 'POCO X2 256GB',
        'POCO X3', 'POCO X3 Black', 'POCO X3 Blue', 'POCO X3 256GB',
        'POCO X3 Pro', 'POCO X3 Pro Black', 'POCO X3 Pro Blue', 'POCO X3 Pro 256GB',
        'POCO X4 Pro', 'POCO X4 Pro Black', 'POCO X4 Pro Blue', 'POCO X4 Pro 256GB',
        'POCO X5', 'POCO X5 Black', 'POCO X5 Blue', 'POCO X5 256GB',
        'POCO X5 Pro', 'POCO X5 Pro Black', 'POCO X5 Pro Blue', 'POCO X5 Pro 256GB',
        'POCO X6', 'POCO X6 Black', 'POCO X6 Blue', 'POCO X6 256GB',
        'POCO X6 Pro', 'POCO X6 Pro Black', 'POCO X6 Pro Blue', 'POCO X6 Pro 256GB',
        'POCO X7', 'POCO X7 Black', 'POCO X7 Blue', 'POCO X7 256GB',
        'POCO X7 Pro', 'POCO X7 Pro Black', 'POCO X7 Pro Blue', 'POCO X7 Pro 256GB',
        'POCO M3', 'POCO M3 Black', 'POCO M3 Blue', 'POCO M3 256GB',
        'POCO M4 Pro', 'POCO M4 Pro Black', 'POCO M4 Pro Blue', 'POCO M4 Pro 256GB',
        'POCO M5', 'POCO M5 Black', 'POCO M5 Blue', 'POCO M5 256GB',
        'POCO M6 Pro', 'POCO M6 Pro Black', 'POCO M6 Pro Blue', 'POCO M6 Pro 256GB',
        'POCO C65', 'POCO C65 Black', 'POCO C65 Blue', 'POCO C65 256GB',
    ],
    Infinix: [
        'Infinix Hot 10', 'Infinix Hot 10 Black', 'Infinix Hot 10 Blue', 'Infinix Hot 10 256GB',
        'Infinix Hot 10S', 'Infinix Hot 10S Black', 'Infinix Hot 10S Blue', 'Infinix Hot 10S 256GB',
        'Infinix Hot 11', 'Infinix Hot 11 Black', 'Infinix Hot 11 Blue', 'Infinix Hot 11 256GB',
        'Infinix Hot 11S', 'Infinix Hot 11S Black', 'Infinix Hot 11S Blue', 'Infinix Hot 11S 256GB',
        'Infinix Hot 12', 'Infinix Hot 12 Black', 'Infinix Hot 12 Blue', 'Infinix Hot 12 256GB',
        'Infinix Hot 20', 'Infinix Hot 20 Black', 'Infinix Hot 20 Blue', 'Infinix Hot 20 256GB',
        'Infinix Hot 20S', 'Infinix Hot 20S Black', 'Infinix Hot 20S Blue', 'Infinix Hot 20S 256GB',
        'Infinix Hot 30', 'Infinix Hot 30 Black', 'Infinix Hot 30 Blue', 'Infinix Hot 30 256GB',
        'Infinix Hot 30i', 'Infinix Hot 30i Black', 'Infinix Hot 30i Blue', 'Infinix Hot 30i 256GB',
        'Infinix Hot 40', 'Infinix Hot 40 Black', 'Infinix Hot 40 Blue', 'Infinix Hot 40 256GB',
        'Infinix Hot 40i', 'Infinix Hot 40i Black', 'Infinix Hot 40i Blue', 'Infinix Hot 40i 256GB',
        'Infinix Hot 40 Pro', 'Infinix Hot 40 Pro Black', 'Infinix Hot 40 Pro Blue', 'Infinix Hot 40 Pro 256GB',
        'Infinix Note 10', 'Infinix Note 10 Black', 'Infinix Note 10 Blue', 'Infinix Note 10 256GB',
        'Infinix Note 10 Pro', 'Infinix Note 10 Pro Black', 'Infinix Note 10 Pro Blue', 'Infinix Note 10 Pro 256GB',
        'Infinix Note 11', 'Infinix Note 11 Black', 'Infinix Note 11 Blue', 'Infinix Note 11 256GB',
        'Infinix Note 12', 'Infinix Note 12 Black', 'Infinix Note 12 Blue', 'Infinix Note 12 256GB',
        'Infinix Note 12 Pro', 'Infinix Note 12 Pro Black', 'Infinix Note 12 Pro Blue', 'Infinix Note 12 Pro 256GB',
        'Infinix Note 30', 'Infinix Note 30 Black', 'Infinix Note 30 Blue', 'Infinix Note 30 256GB',
        'Infinix Note 30 5G', 'Infinix Note 30 5G Black', 'Infinix Note 30 5G Blue', 'Infinix Note 30 5G 256GB',
        'Infinix Note 30 Pro', 'Infinix Note 30 Pro Black', 'Infinix Note 30 Pro Blue', 'Infinix Note 30 Pro 256GB',
        'Infinix Note 40', 'Infinix Note 40 Black', 'Infinix Note 40 Blue', 'Infinix Note 40 256GB',
        'Infinix Note 40 Pro', 'Infinix Note 40 Pro Black', 'Infinix Note 40 Pro Blue', 'Infinix Note 40 Pro 256GB',
        'Infinix Zero 20', 'Infinix Zero 20 Black', 'Infinix Zero 20 Blue', 'Infinix Zero 20 256GB',
        'Infinix Zero 30', 'Infinix Zero 30 Black', 'Infinix Zero 30 Blue', 'Infinix Zero 30 256GB',
        'Infinix GT 10 Pro', 'Infinix GT 10 Pro Black', 'Infinix GT 10 Pro Blue', 'Infinix GT 10 Pro 256GB',
    ],
    Tecno: [
        'Tecno Spark 7', 'Tecno Spark 7 Black', 'Tecno Spark 7 Blue', 'Tecno Spark 7 256GB',
        'Tecno Spark 8', 'Tecno Spark 8 Black', 'Tecno Spark 8 Blue', 'Tecno Spark 8 256GB',
        'Tecno Spark 8 Pro', 'Tecno Spark 8 Pro Black', 'Tecno Spark 8 Pro Blue', 'Tecno Spark 8 Pro 256GB',
        'Tecno Spark 9', 'Tecno Spark 9 Black', 'Tecno Spark 9 Blue', 'Tecno Spark 9 256GB',
        'Tecno Spark 9T', 'Tecno Spark 9T Black', 'Tecno Spark 9T Blue', 'Tecno Spark 9T 256GB',
        'Tecno Spark 10', 'Tecno Spark 10 Black', 'Tecno Spark 10 Blue', 'Tecno Spark 10 256GB',
        'Tecno Spark 10 Pro', 'Tecno Spark 10 Pro Black', 'Tecno Spark 10 Pro Blue', 'Tecno Spark 10 Pro 256GB',
        'Tecno Spark 20', 'Tecno Spark 20 Black', 'Tecno Spark 20 Blue', 'Tecno Spark 20 256GB',
        'Tecno Spark 20C', 'Tecno Spark 20C Black', 'Tecno Spark 20C Blue', 'Tecno Spark 20C 256GB',
        'Tecno Spark 20 Pro', 'Tecno Spark 20 Pro Black', 'Tecno Spark 20 Pro Blue', 'Tecno Spark 20 Pro 256GB',
        'Tecno Spark Go 2023', 'Tecno Spark Go 2023 Black', 'Tecno Spark Go 2023 Blue', 'Tecno Spark Go 2023 256GB',
        'Tecno Spark Go 2024', 'Tecno Spark Go 2024 Black', 'Tecno Spark Go 2024 Blue', 'Tecno Spark Go 2024 256GB',
        'Tecno Camon 17', 'Tecno Camon 17 Black', 'Tecno Camon 17 Blue', 'Tecno Camon 17 256GB',
        'Tecno Camon 18', 'Tecno Camon 18 Black', 'Tecno Camon 18 Blue', 'Tecno Camon 18 256GB',
        'Tecno Camon 19', 'Tecno Camon 19 Black', 'Tecno Camon 19 Blue', 'Tecno Camon 19 256GB',
        'Tecno Camon 19 Pro', 'Tecno Camon 19 Pro Black', 'Tecno Camon 19 Pro Blue', 'Tecno Camon 19 Pro 256GB',
        'Tecno Camon 20', 'Tecno Camon 20 Black', 'Tecno Camon 20 Blue', 'Tecno Camon 20 256GB',
        'Tecno Camon 20 Pro', 'Tecno Camon 20 Pro Black', 'Tecno Camon 20 Pro Blue', 'Tecno Camon 20 Pro 256GB',
        'Tecno Camon 20 Premier', 'Tecno Camon 20 Premier Black', 'Tecno Camon 20 Premier Blue', 'Tecno Camon 20 Premier 256GB',
        'Tecno Camon 30', 'Tecno Camon 30 Black', 'Tecno Camon 30 Blue', 'Tecno Camon 30 256GB',
        'Tecno Camon 30 Pro', 'Tecno Camon 30 Pro Black', 'Tecno Camon 30 Pro Blue', 'Tecno Camon 30 Pro 256GB',
        'Tecno Phantom X', 'Tecno Phantom X Black', 'Tecno Phantom X Blue', 'Tecno Phantom X 256GB',
        'Tecno Phantom X2', 'Tecno Phantom X2 Black', 'Tecno Phantom X2 Blue', 'Tecno Phantom X2 256GB',
        'Tecno Phantom V Fold', 'Tecno Phantom V Fold Black', 'Tecno Phantom V Fold Blue', 'Tecno Phantom V Fold 256GB',
        'Tecno Pova 5 Pro', 'Tecno Pova 5 Pro Black', 'Tecno Pova 5 Pro Blue', 'Tecno Pova 5 Pro 256GB',
    ],
    Nothing: [
        'Nothing Phone (1)', 'Nothing Phone (1) Black', 'Nothing Phone (1) Blue', 'Nothing Phone (1) 256GB',
        'Nothing Phone (2)', 'Nothing Phone (2) Black', 'Nothing Phone (2) Blue', 'Nothing Phone (2) 256GB',
        'Nothing Phone (2a)', 'Nothing Phone (2a) Black', 'Nothing Phone (2a) Blue', 'Nothing Phone (2a) 256GB',
        'Nothing Phone (2a) Plus', 'Nothing Phone (2a) Plus Black', 'Nothing Phone (2a) Plus Blue', 'Nothing Phone (2a) Plus 256GB',
        'Nothing Phone (3)', 'Nothing Phone (3) Black', 'Nothing Phone (3) Blue', 'Nothing Phone (3) 256GB',
        'Nothing Phone (3a)', 'Nothing Phone (3a) Black', 'Nothing Phone (3a) Blue', 'Nothing Phone (3a) 256GB',
        'Nothing Phone (3a) Pro', 'Nothing Phone (3a) Pro Black', 'Nothing Phone (3a) Pro Blue', 'Nothing Phone (3a) Pro 256GB',
        'Nothing Phone (4)', 'Nothing Phone (4) Black', 'Nothing Phone (4) Blue', 'Nothing Phone (4) 256GB',
        'Nothing Phone (4 Pro)', 'Nothing Phone (4 Pro) Black', 'Nothing Phone (4 Pro) Blue', 'Nothing Phone (4 Pro) 256GB',
        'CMF Phone 1', 'CMF Phone 1 Black', 'CMF Phone 1 Blue', 'CMF Phone 1 256GB',
        'CMF Phone 1 Pro', 'CMF Phone 1 Pro Black', 'CMF Phone 1 Pro Blue', 'CMF Phone 1 Pro 256GB',
        'CMF Phone 2', 'CMF Phone 2 Black', 'CMF Phone 2 Blue', 'CMF Phone 2 256GB',
        'CMF Phone 2 Pro', 'CMF Phone 2 Pro Black', 'CMF Phone 2 Pro Blue', 'CMF Phone 2 Pro 256GB',
        'CMF Phone 3', 'CMF Phone 3 Black', 'CMF Phone 3 Blue', 'CMF Phone 3 256GB',
        'Nothing Phone Lite', 'Nothing Phone Lite Black', 'Nothing Phone Lite Blue', 'Nothing Phone Lite 256GB',
        'Nothing Phone SE', 'Nothing Phone SE Black', 'Nothing Phone SE Blue', 'Nothing Phone SE 256GB',
        'Nothing Phone Neo', 'Nothing Phone Neo Black', 'Nothing Phone Neo Blue', 'Nothing Phone Neo 256GB',
        'Nothing Phone Max', 'Nothing Phone Max Black', 'Nothing Phone Max Blue', 'Nothing Phone Max 256GB',
        'Nothing Phone Ultra', 'Nothing Phone Ultra Black', 'Nothing Phone Ultra Blue', 'Nothing Phone Ultra 256GB',
        'CMF Phone 2a', 'CMF Phone 2a Black', 'CMF Phone 2a Blue', 'CMF Phone 2a 256GB',
        'Nothing Phone (1) Limited', 'Nothing Phone (1) Limited Black', 'Nothing Phone (1) Limited Blue', 'Nothing Phone (1) Limited 256GB',
        'Nothing Phone (2) Limited', 'Nothing Phone (2) Limited Black', 'Nothing Phone (2) Limited Blue', 'Nothing Phone (2) Limited 256GB',
        'Nothing Phone (3) Limited', 'Nothing Phone (3) Limited Black', 'Nothing Phone (3) Limited Blue', 'Nothing Phone (3) Limited 256GB',
        'CMF Phone 1 Limited', 'CMF Phone 1 Limited Black', 'CMF Phone 1 Limited Blue', 'CMF Phone 1 Limited 256GB',
        'CMF Phone 2 Limited', 'CMF Phone 2 Limited Black', 'CMF Phone 2 Limited Blue', 'CMF Phone 2 Limited 256GB',
    ],
    Lenovo: [
        'Lenovo A6000', 'Lenovo A6000 Black', 'Lenovo A6000 Blue', 'Lenovo A6000 256GB',
        'Lenovo A6010', 'Lenovo A6010 Black', 'Lenovo A6010 Blue', 'Lenovo A6010 256GB',
        'Lenovo A7000', 'Lenovo A7000 Black', 'Lenovo A7000 Blue', 'Lenovo A7000 256GB',
        'Lenovo K3 Note', 'Lenovo K3 Note Black', 'Lenovo K3 Note Blue', 'Lenovo K3 Note 256GB',
        'Lenovo K4 Note', 'Lenovo K4 Note Black', 'Lenovo K4 Note Blue', 'Lenovo K4 Note 256GB',
        'Lenovo K5', 'Lenovo K5 Black', 'Lenovo K5 Blue', 'Lenovo K5 256GB',
        'Lenovo K5 Note', 'Lenovo K5 Note Black', 'Lenovo K5 Note Blue', 'Lenovo K5 Note 256GB',
        'Lenovo K6', 'Lenovo K6 Black', 'Lenovo K6 Blue', 'Lenovo K6 256GB',
        'Lenovo K6 Power', 'Lenovo K6 Power Black', 'Lenovo K6 Power Blue', 'Lenovo K6 Power 256GB',
        'Lenovo K6 Note', 'Lenovo K6 Note Black', 'Lenovo K6 Note Blue', 'Lenovo K6 Note 256GB',
        'Lenovo K8 Note', 'Lenovo K8 Note Black', 'Lenovo K8 Note Blue', 'Lenovo K8 Note 256GB',
        'Lenovo K8 Plus', 'Lenovo K8 Plus Black', 'Lenovo K8 Plus Blue', 'Lenovo K8 Plus 256GB',
        'Lenovo K9', 'Lenovo K9 Black', 'Lenovo K9 Blue', 'Lenovo K9 256GB',
        'Lenovo K10 Note', 'Lenovo K10 Note Black', 'Lenovo K10 Note Blue', 'Lenovo K10 Note 256GB',
        'Lenovo K11', 'Lenovo K11 Black', 'Lenovo K11 Blue', 'Lenovo K11 256GB',
        'Lenovo K12', 'Lenovo K12 Black', 'Lenovo K12 Blue', 'Lenovo K12 256GB',
        'Lenovo K12 Pro', 'Lenovo K12 Pro Black', 'Lenovo K12 Pro Blue', 'Lenovo K12 Pro 256GB',
        'Lenovo Z5', 'Lenovo Z5 Black', 'Lenovo Z5 Blue', 'Lenovo Z5 256GB',
        'Lenovo Z5 Pro', 'Lenovo Z5 Pro Black', 'Lenovo Z5 Pro Blue', 'Lenovo Z5 Pro 256GB',
        'Lenovo Z6', 'Lenovo Z6 Black', 'Lenovo Z6 Blue', 'Lenovo Z6 256GB',
        'Lenovo Legion Duel', 'Lenovo Legion Duel Black', 'Lenovo Legion Duel Blue', 'Lenovo Legion Duel 256GB',
        'Lenovo Legion Duel 2', 'Lenovo Legion Duel 2 Black', 'Lenovo Legion Duel 2 Blue', 'Lenovo Legion Duel 2 256GB',
        'Lenovo Legion Y90', 'Lenovo Legion Y90 Black', 'Lenovo Legion Y90 Blue', 'Lenovo Legion Y90 256GB',
        'Lenovo Vibe K5', 'Lenovo Vibe K5 Black', 'Lenovo Vibe K5 Blue', 'Lenovo Vibe K5 256GB',
        'Lenovo Phab 2 Pro', 'Lenovo Phab 2 Pro Black', 'Lenovo Phab 2 Pro Blue', 'Lenovo Phab 2 Pro 256GB',
    ],
    Micromax: [
        'Micromax Canvas 2', 'Micromax Canvas 2 Black', 'Micromax Canvas 2 Blue', 'Micromax Canvas 2 256GB',
        'Micromax Canvas 4', 'Micromax Canvas 4 Black', 'Micromax Canvas 4 Blue', 'Micromax Canvas 4 256GB',
        'Micromax Canvas 5', 'Micromax Canvas 5 Black', 'Micromax Canvas 5 Blue', 'Micromax Canvas 5 256GB',
        'Micromax Canvas HD', 'Micromax Canvas HD Black', 'Micromax Canvas HD Blue', 'Micromax Canvas HD 256GB',
        'Micromax Canvas Nitro', 'Micromax Canvas Nitro Black', 'Micromax Canvas Nitro Blue', 'Micromax Canvas Nitro 256GB',
        'Micromax Canvas Spark', 'Micromax Canvas Spark Black', 'Micromax Canvas Spark Blue', 'Micromax Canvas Spark 256GB',
        'Micromax Canvas Selfie', 'Micromax Canvas Selfie Black', 'Micromax Canvas Selfie Blue', 'Micromax Canvas Selfie 256GB',
        'Micromax Canvas Infinity', 'Micromax Canvas Infinity Black', 'Micromax Canvas Infinity Blue', 'Micromax Canvas Infinity 256GB',
        'Micromax Canvas Infinity Pro', 'Micromax Canvas Infinity Pro Black', 'Micromax Canvas Infinity Pro Blue', 'Micromax Canvas Infinity Pro 256GB',
        'Micromax Bharat 2', 'Micromax Bharat 2 Black', 'Micromax Bharat 2 Blue', 'Micromax Bharat 2 256GB',
        'Micromax Bharat 4', 'Micromax Bharat 4 Black', 'Micromax Bharat 4 Blue', 'Micromax Bharat 4 256GB',
        'Micromax Bharat 5', 'Micromax Bharat 5 Black', 'Micromax Bharat 5 Blue', 'Micromax Bharat 5 256GB',
        'Micromax Bharat 5 Pro', 'Micromax Bharat 5 Pro Black', 'Micromax Bharat 5 Pro Blue', 'Micromax Bharat 5 Pro 256GB',
        'Micromax Bharat Go', 'Micromax Bharat Go Black', 'Micromax Bharat Go Blue', 'Micromax Bharat Go 256GB',
        'Micromax Evok Dual Note', 'Micromax Evok Dual Note Black', 'Micromax Evok Dual Note Blue', 'Micromax Evok Dual Note 256GB',
        'Micromax Dual 5', 'Micromax Dual 5 Black', 'Micromax Dual 5 Blue', 'Micromax Dual 5 256GB',
        'Micromax IN 1', 'Micromax IN 1 Black', 'Micromax IN 1 Blue', 'Micromax IN 1 256GB',
        'Micromax IN 1b', 'Micromax IN 1b Black', 'Micromax IN 1b Blue', 'Micromax IN 1b 256GB',
        'Micromax IN 2b', 'Micromax IN 2b Black', 'Micromax IN 2b Blue', 'Micromax IN 2b 256GB',
        'Micromax IN 2c', 'Micromax IN 2c Black', 'Micromax IN 2c Blue', 'Micromax IN 2c 256GB',
        'Micromax IN Note 1', 'Micromax IN Note 1 Black', 'Micromax IN Note 1 Blue', 'Micromax IN Note 1 256GB',
        'Micromax IN Note 2', 'Micromax IN Note 2 Black', 'Micromax IN Note 2 Blue', 'Micromax IN Note 2 256GB',
        'Micromax Canvas Knight', 'Micromax Canvas Knight Black', 'Micromax Canvas Knight Blue', 'Micromax Canvas Knight 256GB',
        'Micromax Unite 2', 'Micromax Unite 2 Black', 'Micromax Unite 2 Blue', 'Micromax Unite 2 256GB',
        'Micromax Unite 4', 'Micromax Unite 4 Black', 'Micromax Unite 4 Blue', 'Micromax Unite 4 256GB',
    ],
    LG: [
        'LG G3', 'LG G3 Black', 'LG G3 Blue', 'LG G3 256GB',
        'LG G4', 'LG G4 Black', 'LG G4 Blue', 'LG G4 256GB',
        'LG G5', 'LG G5 Black', 'LG G5 Blue', 'LG G5 256GB',
        'LG G6', 'LG G6 Black', 'LG G6 Blue', 'LG G6 256GB',
        'LG G7 ThinQ', 'LG G7 ThinQ Black', 'LG G7 ThinQ Blue', 'LG G7 ThinQ 256GB',
        'LG G8 ThinQ', 'LG G8 ThinQ Black', 'LG G8 ThinQ Blue', 'LG G8 ThinQ 256GB',
        'LG G8X ThinQ', 'LG G8X ThinQ Black', 'LG G8X ThinQ Blue', 'LG G8X ThinQ 256GB',
        'LG V20', 'LG V20 Black', 'LG V20 Blue', 'LG V20 256GB',
        'LG V30', 'LG V30 Black', 'LG V30 Blue', 'LG V30 256GB',
        'LG V30+', 'LG V30+ Black', 'LG V30+ Blue', 'LG V30+ 256GB',
        'LG V40 ThinQ', 'LG V40 ThinQ Black', 'LG V40 ThinQ Blue', 'LG V40 ThinQ 256GB',
        'LG V50 ThinQ', 'LG V50 ThinQ Black', 'LG V50 ThinQ Blue', 'LG V50 ThinQ 256GB',
        'LG V60 ThinQ', 'LG V60 ThinQ Black', 'LG V60 ThinQ Blue', 'LG V60 ThinQ 256GB',
        'LG Velvet', 'LG Velvet Black', 'LG Velvet Blue', 'LG Velvet 256GB',
        'LG Velvet 5G', 'LG Velvet 5G Black', 'LG Velvet 5G Blue', 'LG Velvet 5G 256GB',
        'LG Wing', 'LG Wing Black', 'LG Wing Blue', 'LG Wing 256GB',
        'LG Q6', 'LG Q6 Black', 'LG Q6 Blue', 'LG Q6 256GB',
        'LG Q7', 'LG Q7 Black', 'LG Q7 Blue', 'LG Q7 256GB',
        'LG Q8', 'LG Q8 Black', 'LG Q8 Blue', 'LG Q8 256GB',
        'LG Q60', 'LG Q60 Black', 'LG Q60 Blue', 'LG Q60 256GB',
        'LG Q61', 'LG Q61 Black', 'LG Q61 Blue', 'LG Q61 256GB',
        'LG Q70', 'LG Q70 Black', 'LG Q70 Blue', 'LG Q70 256GB',
        'LG Q92', 'LG Q92 Black', 'LG Q92 Blue', 'LG Q92 256GB',
        'LG Stylo 6', 'LG Stylo 6 Black', 'LG Stylo 6 Blue', 'LG Stylo 6 256GB',
        'LG Nexus 5X', 'LG Nexus 5X Black', 'LG Nexus 5X Blue', 'LG Nexus 5X 256GB',
    ],
};

const DEVICE_MAKE_OPTIONS: DeviceMakeOption[] = Object.keys(DEVICE_MAKE_MODEL_CATALOG).map(make => ({
    id: make,
    name: make,
}));

const getAllowedDeviceModels = (selectedMakes: string[]): string[] =>
    Array.from(new Set(
        selectedMakes.flatMap(makeId => DEVICE_MAKE_MODEL_CATALOG[makeId] ?? []),
    ));

const FILE_FORMAT_GROUPS: { label: string; formats: string[] }[] = [
    { label: 'Image',    formats: ['JPEG', 'JPG', 'PNG', 'HEIF', 'HEIC'] },
    { label: 'Audio',    formats: ['MP3', 'WAV', 'AAC'] },
    { label: 'Video',    formats: ['MP4', 'MOV', 'AVI', 'HEVC'] },
    { label: 'Document', formats: ['PDF', 'DOCX', 'DOC', 'XLSX', 'EML'] },
];

const FILE_FORMAT_OPTIONS = FILE_FORMAT_GROUPS.flatMap(g => g.formats);

const FILE_FORMAT_META: Record<string, { iconBg: string; ext: string }> = {
    JPEG: { iconBg: '#5b9ecf', ext: '.jpeg' },
    JPG:  { iconBg: '#4a87be', ext: '.jpg'  },
    PNG:  { iconBg: '#7b72b8', ext: '.png'  },
    HEIF: { iconBg: '#5a7a9e', ext: '.heif' },
    HEIC: { iconBg: '#4d6e8c', ext: '.heic' },
    MP3:  { iconBg: '#8a72b8', ext: '.mp3'  },
    WAV:  { iconBg: '#b87070', ext: '.wav'  },
    AAC:  { iconBg: '#c48f60', ext: '.aac'  },
    MP4:  { iconBg: '#7a6aae', ext: '.mp4'  },
    MOV:  { iconBg: '#b89060', ext: '.mov'  },
    AVI:  { iconBg: '#5a9a72', ext: '.avi'  },
    HEVC: { iconBg: '#6a5a9e', ext: '.hevc' },
    PDF:  { iconBg: '#bf6060', ext: '.pdf'  },
    DOCX: { iconBg: '#4a7bbf', ext: '.docx' },
    DOC:  { iconBg: '#5080b0', ext: '.doc'  },
    XLSX: { iconBg: '#5a9a72', ext: '.xlsx' },
    EML:  { iconBg: '#4a8fa5', ext: '.eml'  },
};

const AI_MODELS = [
    'ChatGPT 5', 'Claude 3.7', 'Claude 4.2', 'Nova Pro', 'Nova Premier', 'Gemini 2.5', 'Claude 4.0',
];

// const AI_MODEL_IDENTIFIERS: Record<string, string> = {
//     'Claude 4.0': 'claude-sonnet-4-20250514',
//     'ChatGPT 5': 'gpt-5.4-2026-03-05',
// };

const COUNTRIES: string[] = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
    'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain',
    'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
    'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
    'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada',
    'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros',
    'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
    'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica',
    'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea',
    'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
    'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
    'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras',
    'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
    'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya',
    'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho',
    'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar',
    'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
    'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco',
    'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
    'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger',
    'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
    'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
    'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
    'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
    'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
    'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
    'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
    'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo',
    'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
    'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
    'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
    'Yemen', 'Zambia', 'Zimbabwe',
];

const CAPTURE_DATE_OPTIONS: { value: string; label: string }[] = [
    { value: 'last_15', label: 'Last 15 days' },
    { value: 'last_30', label: 'Last 30 days' },
    { value: 'last_45', label: 'Last 45 days' },
    { value: 'last_60', label: 'Last 60 days' },
    { value: 'last_90', label: 'Last 90 days' },
];

interface MetadataField {
    id: string;
    label: string;
    enabled: boolean;
    operator: string;
    value: string;
    required: boolean;
    operatorOptions: string[];
    valueType: ValueType;
    minValue?: string;
    maxValue?: string;
}

type DuplicationType = 'custom_literal_path' | 'custom_template_path';
type ResolutionMode = 'current_path_only' | 'all_resolved_paths';

interface AdditionalValidation {
    id: string;
    label: string;
    enabled: boolean;
    schedule: Schedule;
    duplicationType?: DuplicationType;
    literalPath?: string;
    literalPaths?: string[];
    templatePath?: string;
    templatePaths?: string[];
    resolutionMode?: ResolutionMode;
}

interface AIRules {
    failThreshold: string;
    partialMin: string;
    partialMax: string;
    fullPassThreshold: string;
    fullPassAction: FullPassAction;
}

export interface AdvancedAiConfig {
    metadataFields: MetadataField[];
    customPrompt: {
        enabled: boolean;
        text: string;
        schedule: Schedule;
    };
    additionalValidations: AdditionalValidation[];
    aiRules: AIRules;
    hasMetadataErrors?: boolean;
    hasDuplicationPathError?: boolean;
    model?: string;
    pipelineOption?: 'pipeline1' | 'pipeline2';
}

const ALLOWED_CONFIG_TYPES = [
    'radio', 'checkbox', 'edit_content', 'single_select',
    'multi_select', 'multi_level_dropdown', 'content',
] as const;


const DYNAMIC_VALUE_TYPES = ['content', 'paragraph', 'edit_content', 'single_select', 'multi_select', 'checkbox', 'radio'] as const;

interface AdvancedAiSettingsProps {
    onSave?: (config: object) => void;
    onCancel?: () => void;
    onConfigChange?: (config: AdvancedAiConfig) => void;
    initialConfig?: AdvancedAiConfig;
    configIdentifiers?: { id: string; identifier: string; type?: string; value?: Record<string, unknown> }[];
    useCustomStorage?: boolean;
    endpointUrlInput?: string;
}


const DEFAULT_METADATA_FIELDS: MetadataField[] = [
    {
        id: 'resolution',
        label: 'Resolution (MP)',
        enabled: false,
        operator: '',
        value: '',
        required: false,
        operatorOptions: [],
        valueType: 'number',
        minValue: '',
        maxValue: '20',
    },
    {
        id: 'filesize',
        label: 'File Size (MB)',
        enabled: false,
        operator: '',
        value: '',
        required: false,
        operatorOptions: [],
        valueType: 'number',
        minValue: '',
        maxValue: '50',
    },
    {
        id: 'location',
        label: 'Location',
        enabled: false,
        operator: '',
        value: '',
        required: false,
        operatorOptions: [],
        valueType: 'text',
    },
    {
        id: 'fileformat',
        label: 'File Format',
        enabled: true,
        operator: 'In',
        value: '',
        required: false,
        operatorOptions: [],
        valueType: 'multiselect',
    },
    {
        id: 'capturedate',
        label: 'Capture Date',
        enabled: false,
        operator: '',
        value: '',
        required: false,
        operatorOptions: [],
        valueType: 'days_select',
    },
    {
        id: 'devicemake',
        label: 'Device Make',
        enabled: false,
        operator: '',
        value: '',
        required: false,
        operatorOptions: [],
        valueType: 'multiselect',
    },
    {
        id: 'devicemodel',
        label: 'Device Model',
        enabled: false,
        operator: '',
        value: '',
        required: false,
        operatorOptions: [],
        valueType: 'multiselect',
    },
];

const DEFAULT_ADDITIONAL: AdditionalValidation[] = [
    { id: 'blur', label: 'Blur Check', enabled: false, schedule: 'on_image_upload' },
    { id: 'pii', label: 'PII Detection', enabled: false, schedule: 'on_image_upload' },
    { id: 'duplication', label: 'Duplication Check', enabled: false, schedule: 'on_image_upload', duplicationType: 'custom_literal_path', literalPaths: [''], templatePaths: [''], resolutionMode: 'current_path_only' },
];

const DEFAULT_AI_RULES: AIRules = {
    failThreshold: '70',
    partialMin: '70',
    partialMax: '95',
    fullPassThreshold: '95',
    fullPassAction: 'send_to_next',
};

function loadPersistedConfig() {
    try {
        const raw = localStorage.getItem('advancedAiValidationConfig');
        if (raw) return JSON.parse(raw);
    } catch {
        // ignore
    }
    return null;
}

function normalizeSchedule(s: string): Schedule {
    if (s === 'on_task_submission') return 'on_task_submission';
    return 'on_image_upload';
}

function mergeMetadataFieldsWithDefaults(fields: MetadataField[]): MetadataField[] {
    return DEFAULT_METADATA_FIELDS.map(defaultField => {
        const incoming = fields.find(f => f.id === defaultField.id);
        if (!incoming) return defaultField;
        return {
            ...defaultField,
            ...incoming,
            minValue: incoming.minValue ?? defaultField.minValue,
            maxValue: incoming.maxValue ?? defaultField.maxValue,
        };
    });
}

function normalizeAdditional(validations: AdditionalValidation[]): AdditionalValidation[] {
    return validations
        .filter(v => v.label !== 'Content Safety')
        .map(v => {
            const def = DEFAULT_ADDITIONAL.find(d => d.id === v.id);
            return {
                ...def,
                ...v,
                schedule: normalizeSchedule(v.schedule),
                duplicationType: v.duplicationType ?? def?.duplicationType,
                templatePaths: v.templatePaths ?? (v.templatePath ? [v.templatePath] : def?.templatePaths ?? ['']),
                literalPaths: v.literalPaths ?? (v.literalPath ? [v.literalPath] : def?.literalPaths ?? ['']),
                resolutionMode: v.resolutionMode ?? def?.resolutionMode ?? 'current_path_only',
            };
        });
}

const sectionPaperSx = {
    p: { xs: 1.5, md: 2.5 },
    borderRadius: 2,
    border: '1px solid #e5e7eb',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: '#ffffff',
};

const sectionTitleSx = {
    fontWeight: 700,
    fontSize: '13px',
    color: '#111827',
    mb: 0.5,
    pl: 1.25,
    borderLeft: '3px solid #3D85C6',
    lineHeight: 1.4,
};

const sectionSubtitleSx = {
    fontSize: '11px',
    color: '#9ca3af',
    mb: 2,
    pl: 0.25,
};



const DUPLICATION_PREFIX = 'project_id/';

interface TemplatePathFieldProps {
    value: string;
    onChange: (val: string) => void;
    disabled: boolean;
    endpointUrlInput: string;
    useCustomStorage: boolean;
    showWarning?: boolean;
    isLast?: boolean;
}

const TemplatePathField: React.FC<TemplatePathFieldProps> = ({
    value,
    onChange,
    disabled,
    endpointUrlInput,
    useCustomStorage,
    showWarning = true,
    isLast = false,
}) => {
    const [mentionAnchorEl, setMentionAnchorEl] = useState<HTMLElement | null>(null);
    const [mentionQuery, setMentionQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const endpointVariables = useMemo(() => {
        if (!endpointUrlInput) return [];
        return (endpointUrlInput.match(/\{([^}]+)\}/g) || []).map((m: string) => m.slice(1, -1));
    }, [endpointUrlInput]);

    useEffect(() => {
        if (!useCustomStorage && !value.startsWith(DUPLICATION_PREFIX)) {
            onChange(DUPLICATION_PREFIX);
        }
    }, [useCustomStorage]); // eslint-disable-line react-hooks/exhaustive-deps

    const hasWildcard = value.includes('*');


    const availableVariables = useMemo(() => {
        const usedIndices = (value.match(/\{([^}]+)\}/g) || [])
            .map((m: string) => endpointVariables.indexOf(m.slice(1, -1)))
            .filter((idx: number) => idx >= 0);
        const lastUsedIndex = usedIndices.length > 0 ? Math.max(...usedIndices) : -1;
        return endpointVariables.filter((_: string, idx: number) => idx > lastUsedIndex);
    }, [value, endpointVariables]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.value;

        if (!useCustomStorage && !newVal.startsWith(DUPLICATION_PREFIX)) return;
        if (hasWildcard && newVal.length > value.length) return;

        const cursorPos = e.target.selectionStart ?? newVal.length;
        const atIndex = newVal.lastIndexOf('@', cursorPos - 1);
        if (atIndex >= 0) {
            const textAfterAt = newVal.slice(atIndex + 1, cursorPos);
            if (!/[\s/]/.test(textAfterAt)) {
                setMentionAnchorEl(inputRef.current);
                setMentionQuery(textAfterAt);
            } else {
                setMentionAnchorEl(null);
            }
        } else {
            setMentionAnchorEl(null);
        }

        onChange(newVal);
    };

    const handleSelectVariable = (variable: string) => {
        const atIndex = value.lastIndexOf('@');
        const newVal = (atIndex >= 0 ? value.slice(0, atIndex) : value) + `{${variable}}/`;
        onChange(newVal);
        setMentionAnchorEl(null);
        setMentionQuery('');
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const filteredVariables = availableVariables.filter((v: string) =>
        v.toLowerCase().includes(mentionQuery.toLowerCase())
    );

    const displayValue = (!useCustomStorage && !value) ? DUPLICATION_PREFIX : value;

    const helperText = (useCustomStorage && isLast)
        ? hasWildcard
            ? 'Remove * to continue editing.'
            : availableVariables.length > 0
                ? 'Type @ to pick next variable. Use * to match all remaining segments.'
                : 'All variables used.'
        : undefined;

    return (
        <Box sx={{ flex: 1 }}>
            {showWarning && !useCustomStorage && (
                <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.75,
                    p: 0.75, backgroundColor: '#fef9c3', borderRadius: 1, border: '1px solid #fde047',
                }}>
                    <WarningAmberOutlined sx={{ fontSize: 13, color: '#b45309', mt: '1px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '10px', color: '#92400e', lineHeight: 1.4 }}>
                        Custom Storage Provider not selected. Data will be checked under root folder (<strong>project_id</strong>).
                    </Typography>
                </Box>
            )}
            <TextField
                inputRef={inputRef}
                size="small"
                fullWidth
                disabled={disabled || !useCustomStorage}
                value={displayValue}
                onChange={handleInputChange}
                placeholder={useCustomStorage ? 'Type @ to insert a variable, e.g. {today_date}/{tag_1}/' : DUPLICATION_PREFIX}
                helperText={helperText}
                FormHelperTextProps={{ sx: { fontSize: '10px', mx: 0 } }}
                sx={{
                    '& .MuiInputBase-input': { fontSize: '11px' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                }}
            />
            <Menu
                anchorEl={mentionAnchorEl}
                open={Boolean(mentionAnchorEl) && availableVariables.length > 0}
                onClose={() => { setMentionAnchorEl(null); setMentionQuery(''); }}
                PaperProps={{ sx: { minWidth: 180 } }}
                disableAutoFocus
                disableEnforceFocus
            >
                {(filteredVariables.length > 0 ? filteredVariables : availableVariables).map((v: string) => (
                    <MenuItem key={v} onClick={() => handleSelectVariable(v)} sx={{ fontSize: '11px' }}>
                        {`{${v}}`}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};


// ── Prompt editor helpers (module-level — no state/props dependencies) ──────

const rawToHtml = (raw: string): string =>
    raw.split(/(\{[^}]+\})/g).map(part => {
        const m = part.match(/^\{([^}]+)\}$/);
        if (m) {
            return `<span class="prompt-token" data-value="{${m[1]}}" contenteditable="false" style="display:inline-flex;align-items:center;background:#dbeafe;color:#1d4ed8;font-size:11px;font-weight:600;padding:1px 8px;border-radius:12px;border:1px solid #93c5fd;line-height:1.6;cursor:default;user-select:none;white-space:nowrap;vertical-align:middle;margin:0 1px">${m[1]}</span>`;
        }
        return part.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }).join('');

const extractRawFromEditor = (el: HTMLDivElement): string => {
    let text = '';
    const traverse = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent ?? '';
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const htmlEl = node as HTMLElement;
            if (htmlEl.dataset.value) {
                text += htmlEl.dataset.value;
            } else if (htmlEl.tagName === 'BR') {
                text += '\n';
            } else if (htmlEl.tagName === 'DIV' || htmlEl.tagName === 'P') {
                if (text.length > 0 && !text.endsWith('\n')) text += '\n';
                htmlEl.childNodes.forEach(traverse);
            } else {
                htmlEl.childNodes.forEach(traverse);
            }
        }
    };
    el.childNodes.forEach(traverse);
    return text;
};

const MULTISELECT_SEPARATOR = ',';

const splitMultiselectValue = (value: string): string[] =>
    value ? value.split(MULTISELECT_SEPARATOR).map(v => v.trim()).filter(Boolean) : [];

const joinMultiselectValue = (values: string[]): string =>
    values.join(MULTISELECT_SEPARATOR);

const AdvancedAiSettings: React.FC<AdvancedAiSettingsProps> = ({ onSave, onCancel, onConfigChange, initialConfig, configIdentifiers = [], useCustomStorage = false, endpointUrlInput = '' }) => {
    const persisted = loadPersistedConfig();

    const [metadataFields, setMetadataFields] = useState<MetadataField[]>(
        DEFAULT_METADATA_FIELDS.map(defaultField => {
            const saved = persisted?.metadataFields?.find((f: any) => f.id === defaultField.id);
            if (!saved) return defaultField;
            return {
                ...defaultField,
                enabled: saved.enabled ?? defaultField.enabled,
                value: saved.value ?? defaultField.value,
                ...(defaultField.minValue !== undefined && { minValue: saved.minValue ?? defaultField.minValue }),
                ...(defaultField.maxValue !== undefined && { maxValue: saved.maxValue ?? defaultField.maxValue }),
            };
        }),
    );

    const [promptEnabled, setPromptEnabled] = useState<boolean>(
        persisted?.customPrompt?.enabled ?? false,
    );
    const [promptText, setPromptText] = useState<string>(persisted?.customPrompt?.text ?? '');
    const [promptSchedule, setPromptSchedule] = useState<Schedule>(
        normalizeSchedule(persisted?.customPrompt?.schedule ?? 'on_image_upload'),
    );
    const [showHint, setShowHint] = useState(false);

    // ── @ mention state ────────────────────────────────────────────────────────
    const editorRef = useRef<HTMLDivElement | null>(null);
    const lastInternalRawRef = useRef<string>('');
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionFilter, setMentionFilter] = useState('');

    const allowedIdentifiers = configIdentifiers.filter(c =>
        !c.type || (ALLOWED_CONFIG_TYPES as readonly string[]).includes(c.type)
    );

    const filteredIdentifiers = mentionFilter
        ? allowedIdentifiers.filter(c =>
              c.identifier.toLowerCase().includes(mentionFilter.toLowerCase())
          )
        : allowedIdentifiers;

    const hasDynamicValueWarning = useMemo(() => {
        const matches = promptText.match(/\{([^}]+)\}/g);
        if (!matches) return false;
        return matches.some(match => {
            const identifier = match.slice(1, -1);
            const config = configIdentifiers.find(c => c.identifier === identifier);
            return config != null && (DYNAMIC_VALUE_TYPES as readonly string[]).includes(config.type ?? '');
        });
    }, [promptText, configIdentifiers]);

    const handleEditorInput = () => {
        const el = editorRef.current;
        if (!el) return;
        const raw = extractRawFromEditor(el);
        lastInternalRawRef.current = raw;
        setPromptText(raw);
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) { setShowMentionDropdown(false); return; }
        const range = selection.getRangeAt(0);
        const node = range.startContainer;
        if (node.nodeType !== Node.TEXT_NODE) { setShowMentionDropdown(false); return; }
        const textBeforeCursor = (node.textContent ?? '').slice(0, range.startOffset);
        const atIdx = textBeforeCursor.lastIndexOf('@');
        if (atIdx !== -1) {
            const textAfterAt = textBeforeCursor.slice(atIdx + 1);
            if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                setMentionFilter(textAfterAt);
                setShowMentionDropdown(true);
                return;
            }
        }
        setShowMentionDropdown(false);
    };

    const handleMentionSelect = (identifier: string) => {
        const el = editorRef.current;
        if (!el) return;
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) return;
        const range = selection.getRangeAt(0);
        const node = range.startContainer;
        if (node.nodeType !== Node.TEXT_NODE) return;
        const offset = range.startOffset;
        const atIdx = (node.textContent ?? '').slice(0, offset).lastIndexOf('@');
        if (atIdx === -1) return;
        const deleteRange = document.createRange();
        deleteRange.setStart(node, atIdx);
        deleteRange.setEnd(node, offset);
        deleteRange.deleteContents();
        const tokenSpan = document.createElement('span');
        tokenSpan.className = 'prompt-token';
        tokenSpan.setAttribute('data-value', `{${identifier}}`);
        tokenSpan.setAttribute('contenteditable', 'false');
        tokenSpan.style.cssText = 'display:inline-flex;align-items:center;background:#dbeafe;color:#1d4ed8;font-size:11px;font-weight:600;padding:1px 8px;border-radius:12px;border:1px solid #93c5fd;line-height:1.6;cursor:default;user-select:none;white-space:nowrap;vertical-align:middle;margin:0 1px';
        tokenSpan.textContent = identifier;
        const afterNode = document.createTextNode('\u00A0');
        deleteRange.insertNode(afterNode);
        deleteRange.insertNode(tokenSpan);
        const newRange = document.createRange();
        newRange.setStartAfter(afterNode);
        newRange.setEndAfter(afterNode);
        selection.removeAllRanges();
        selection.addRange(newRange);
        const raw = extractRawFromEditor(el);
        lastInternalRawRef.current = raw;
        setPromptText(raw);
        setShowMentionDropdown(false);
        setMentionFilter('');
        el.focus();
    };

    const [additionalValidations, setAdditionalValidations] = useState<AdditionalValidation[]>(
        persisted?.additionalValidations
            ? normalizeAdditional(persisted.additionalValidations as AdditionalValidation[])
            : DEFAULT_ADDITIONAL,
    );

    const [aiRules, setAIRules] = useState<AIRules>(persisted?.aiRules ?? DEFAULT_AI_RULES);
    const [aiModel, setAiModel] = useState<string>('ChatGPT 5');
    const [locationSearch, setLocationSearch] = useState('');
    const [deviceModelSearch, setDeviceModelSearch] = useState('');


    const initialAppliedRef = useRef(false);
    useEffect(() => {
        if (!initialConfig || initialAppliedRef.current) return;
        initialAppliedRef.current = true;
        setMetadataFields(mergeMetadataFieldsWithDefaults(initialConfig.metadataFields));
        setPromptEnabled(initialConfig.customPrompt.enabled);
        setPromptText(initialConfig.customPrompt.text);
        setPromptSchedule(normalizeSchedule(initialConfig.customPrompt.schedule));
        setAdditionalValidations(
            normalizeAdditional(initialConfig.additionalValidations),
        );
        setAIRules(initialConfig.aiRules);
        setAiModel(initialConfig.model || 'ChatGPT 5');
    }, [initialConfig]);

    // Sync external promptText changes (e.g. from loaded config) into contentEditable
    useEffect(() => {
        if (!editorRef.current) return;
        if (promptText === lastInternalRawRef.current) return;
        editorRef.current.innerHTML = rawToHtml(promptText);
    }, [promptText]);

    const metadataErrors = useMemo(() => {
        const errors: Record<string, string> = {};
        const selectedMakes = splitMultiselectValue(
            metadataFields.find(field => field.id === 'devicemake')?.value ?? '',
        );

        metadataFields.forEach(field => {
            if (!field.enabled) return;
            if (field.id === 'capturedate') return;
            if (field.valueType === 'number') {
                if ((field.minValue ?? '') === '' && (field.maxValue ?? '') === '') {
                    errors[field.id] = 'At least one value is required when enabled.';
                }
            } else if (field.valueType === 'text') {
                if (!field.value.trim()) {
                    errors[field.id] = 'Value is required when enabled.';
                }
            } else if (field.valueType === 'multiselect') {
                const selectedValues = splitMultiselectValue(field.value);

                if (field.id === 'devicemodel' && selectedMakes.length === 0) {
                    errors[field.id] = 'Select at least one Device Make first.';
                    return;
                }

                if (field.id !== 'fileformat' && selectedValues.length === 0) {
                    errors[field.id] = 'At least one value is required when enabled.';
                }
            }
        });
        return errors;
    }, [metadataFields]);

    const hasDuplicationPathError = useMemo(() => {
        const duplication = additionalValidations.find(v => v.id === 'duplication');
        if (!duplication?.enabled) return false;
        if (duplication.duplicationType === 'custom_template_path') {
            return !(duplication.templatePaths ?? []).some(p => p.trim() !== '');
        }
        return !(duplication.literalPaths ?? []).some(p => p.trim() !== '');
    }, [additionalValidations]);

    useEffect(() => {
        if (!onConfigChange) return;
        onConfigChange({
            metadataFields,
            customPrompt: {
                enabled: promptEnabled,
                text: promptText,
                schedule: promptSchedule,
            },
            additionalValidations,
            aiRules,
            hasMetadataErrors: Object.keys(metadataErrors).length > 0,
            hasDuplicationPathError,
            // model: AI_MODEL_IDENTIFIERS[aiModel] ?? aiModel,
            model: aiModel,
            pipelineOption: hasDynamicValueWarning ? 'pipeline2' : 'pipeline1',
        });
    }, [metadataFields, promptEnabled, promptText, promptSchedule, additionalValidations, aiRules, onConfigChange, metadataErrors, aiModel, hasDynamicValueWarning, hasDuplicationPathError]);

    const deviceMakeValue = metadataFields.find(f => f.id === 'devicemake')?.value ?? '';
    const deviceMakeOptions = useMemo(() => DEVICE_MAKE_OPTIONS, []);
    const deviceModelOptions = useMemo(() => {
        const selectedMakeIds = splitMultiselectValue(deviceMakeValue);
        return selectedMakeIds.flatMap(makeId =>
            (DEVICE_MAKE_MODEL_CATALOG[makeId] ?? []).map(modelName => ({
                id: modelName,
                name: modelName,
                make: makeId,
            })),
        );
    }, [deviceMakeValue]);

    useEffect(() => {
        const selectedMakeIds = splitMultiselectValue(deviceMakeValue);
        if (selectedMakeIds.length === 0) {
            setDeviceModelSearch('');
            setMetadataFields(prev =>
                prev.map(f => (f.id === 'devicemodel' && f.value !== '' ? { ...f, value: '' } : f)),
            );
            return;
        }

        const validIds = new Set(
            getAllowedDeviceModels(selectedMakeIds),
        );

        setMetadataFields(prev => prev.map(f => {
            if (f.id !== 'devicemodel') return f;
            const currentIds = splitMultiselectValue(f.value);
            const filtered = currentIds.filter(modelId => validIds.has(modelId));
            return filtered.length === currentIds.length
                ? f
                : { ...f, value: joinMultiselectValue(filtered) };
        }));
    }, [deviceMakeValue]);

    const updateMetadataField = (id: string, updates: Partial<MetadataField>) => {
        setMetadataFields(prev => {
            const nextFields = prev.map(f => (f.id === id ? { ...f, ...updates } : f));

            if (id === 'devicemake' && updates.enabled === false) {
                return nextFields.map(field => {
                    if (field.id !== 'devicemodel') {
                        return field;
                    }

                    return {
                        ...field,
                        enabled: false,
                        value: '',
                    };
                });
            }

            if (id !== 'devicemake') {
                return nextFields;
            }

            const nextDeviceMakeValue = typeof updates.value === 'string'
                ? updates.value
                : nextFields.find(field => field.id === 'devicemake')?.value ?? '';
            const allowedDeviceModels = new Set(
                getAllowedDeviceModels(splitMultiselectValue(nextDeviceMakeValue)),
            );

            return nextFields.map(field => {
                if (field.id !== 'devicemodel') {
                    return field;
                }

                const nextSelectedModels = splitMultiselectValue(field.value).filter(model =>
                    allowedDeviceModels.has(model),
                );

                return nextSelectedModels.length === splitMultiselectValue(field.value).length
                    ? field
                    : { ...field, value: joinMultiselectValue(nextSelectedModels) };
            });
        });
    };

    const updateAdditionalValidation = (id: string, updates: Partial<AdditionalValidation>) => {
        setAdditionalValidations(prev => prev.map(v => (v.id === id ? { ...v, ...updates } : v)));
    };

    const handleAIRuleChange = (field: keyof AIRules, value: string) => {
        setAIRules(prev => ({ ...prev, [field]: value }));
    };

    const handleNumericChange = (field: keyof AIRules) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (val === '' || /^\d{0,3}$/.test(val)) {
            handleAIRuleChange(field, val);
        }
    };

    const handleMetadataNumericChange =
        (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            if (val === '' || /^\d*\.?\d*$/.test(val)) {
                updateMetadataField(id, { value: val });
            }
        };

    return (
        <>
            <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, backgroundColor: '#f3f4f6', overflowX: 'hidden' }}>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, color: '#111827', fontSize: '18px', mb: 0.5 }}
                        >
                            Advanced Validation
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                            Configure metadata rules, prompt validation, and AI confidence thresholds.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SmartToyOutlined sx={{ fontSize: 15, color: '#3D85C6', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
                            AI Model
                        </Typography>
                        <Select
                            variant="standard"
                            value={aiModel}
                            onChange={(e) => setAiModel(e.target.value)}
                            disableUnderline={false}
                            sx={{
                                minWidth: 160,
                                fontSize: '13px',
                                fontFamily: 'Montserrat',
                                fontWeight: 600,
                                color: '#111827',
                                '&:before': { borderBottomColor: '#d1d5db' },
                                '&:hover:not(.Mui-disabled):before': { borderBottomColor: '#3D85C6' },
                                '&:after': { borderBottomColor: '#3D85C6' },
                                '& .MuiSelect-icon': { color: '#6b7280', fontSize: 20 },
                                '& .MuiSelect-select': { pb: '2px' },
                            }}
                        >
                            {AI_MODELS.map(m => (
                                <MenuItem key={m} value={m} sx={{ fontSize: '13px', fontFamily: 'Montserrat' }}>{m}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>

                <Grid container spacing={2} alignItems="stretch">

                    <Grid item xs={12} md={6}>
                        <Paper sx={sectionPaperSx} elevation={0}>
                            <Typography sx={sectionTitleSx}>Field / Metadata Validations (Non AI Validations)</Typography>
                            <Typography sx={sectionSubtitleSx}>Define rules for metadata level validations.</Typography>

                            <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            backgroundColor: '#f8fafc',
                                            '& th': {
                                                borderBottom: '2px solid #e5e7eb',
                                                py: 1,
                                                px: 0.75,
                                            },
                                        }}
                                    >
                                        <TableCell sx={{ fontSize: '11px', color: '#374151', fontWeight: 700, width: '26%', letterSpacing: '0.02em' }}>
                                            Metadata Tag
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '11px', color: '#374151', fontWeight: 700, width: '62%', letterSpacing: '0.02em' }}>
                                            Value
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '11px', color: '#374151', fontWeight: 700, width: '12%', letterSpacing: '0.02em' }}>
                                            Enable
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {metadataFields.map(field => {
                                        const isRangeField = field.id === 'resolution' || field.id === 'filesize';
                                        const isFileFormat = field.id === 'fileformat';
                                        const isLocation = field.id === 'location';
                                        const isDeviceMake = field.id === 'devicemake';
                                        const isDeviceModel = field.id === 'devicemodel';
                                        const deviceMakeField = metadataFields.find(f => f.id === 'devicemake');
                                        const isDeviceModelToggleDisabled = isDeviceModel && !deviceMakeField?.enabled;
                                        const selectedDeviceMakes = splitMultiselectValue(
                                            metadataFields.find(f => f.id === 'devicemake')?.value ?? '',
                                        );
                                        const selectedDeviceModels = isDeviceModel
                                            ? splitMultiselectValue(field.value)
                                            : [];
                                        const selectedCountries = isLocation && field.value
                                            ? field.value.split(',').map(v => v.trim()).filter(v => COUNTRIES.includes(v))
                                            : [];
                                        const filteredCountries = isLocation
                                            ? (locationSearch ? COUNTRIES.filter(c => c.toLowerCase().includes(locationSearch.toLowerCase())) : COUNTRIES)
                                            : [];
                                        const locationGrouped: Record<string, string[]> = {};
                                        filteredCountries.forEach(c => {
                                            const letter = c[0].toUpperCase();
                                            if (!locationGrouped[letter]) locationGrouped[letter] = [];
                                            locationGrouped[letter].push(c);
                                        });
                                        const visibleChips = selectedCountries;
                                        return (
                                        <TableRow
                                            key={field.id}
                                            sx={{
                                                opacity: field.enabled ? 1 : 0.45,
                                                transition: 'opacity 0.2s, background-color 0.15s',
                                                '&:hover td': { backgroundColor: '#f8fafc' },
                                                '& td': { borderBottom: '1px solid #f3f4f6', py: 0.75, px: 0.75 },
                                            }}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography sx={{ fontSize: '11px', color: field.enabled ? '#111827' : '#9ca3af', fontWeight: field.enabled ? 600 : 400, transition: 'color 0.2s' }}>
                                                        {field.label}
                                                    </Typography>
                                                    {field.id === 'location' && (
                                                        <Tooltip
                                                            title="Select one or more countries. Values are stored as comma-separated names."
                                                            placement="right"
                                                            arrow
                                                        >
                                                            <InfoOutlined sx={{ fontSize: 13, color: '#9ca3af', cursor: 'help', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>

                                            <TableCell>
                                                {isRangeField ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                                                            <Typography sx={{ fontSize: '10px', color: '#6b7280', whiteSpace: 'nowrap', fontWeight: 600 }}>Min</Typography>
                                                            <TextField
                                                                size="small"
                                                                disabled={!field.enabled}
                                                                placeholder="Min"
                                                                value={field.minValue ?? ''}
                                                                onChange={e => {
                                                                    const val = e.target.value;
                                                                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                                                        updateMetadataField(field.id, { minValue: val });
                                                                    }
                                                                }}
                                                                inputProps={{ inputMode: 'numeric' }}
                                                                sx={{
                                                                    flex: 1,
                                                                    '& .MuiInputBase-input': { fontSize: '11px', py: '2px', px: '6px' },
                                                                    '& .MuiInputBase-root': { height: 26 },
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                                                            <Typography sx={{ fontSize: '10px', color: '#6b7280', whiteSpace: 'nowrap', fontWeight: 600 }}>Max</Typography>
                                                            <TextField
                                                                size="small"
                                                                disabled={!field.enabled}
                                                                placeholder="Max"
                                                                value={field.maxValue ?? ''}
                                                                onChange={e => {
                                                                    const val = e.target.value;
                                                                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                                                        updateMetadataField(field.id, { maxValue: val });
                                                                    }
                                                                }}
                                                                inputProps={{ inputMode: 'numeric' }}
                                                                sx={{
                                                                    flex: 1,
                                                                    '& .MuiInputBase-input': { fontSize: '11px', py: '2px', px: '6px' },
                                                                    '& .MuiInputBase-root': { height: 26 },
                                                                }}
                                                            />
                                                        </Box>
                                                        </Box>
                                                        {metadataErrors[field.id] && (
                                                            <Typography sx={{ fontSize: '10px', color: '#ef4444' }}>
                                                                {metadataErrors[field.id]}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ) : isFileFormat ? (
                                                    <Select
                                                        multiple
                                                        size="small"
                                                        value={field.value ? field.value.split(',').map(v => v.trim()).filter(v => FILE_FORMAT_OPTIONS.includes(v)) : []}
                                                        onChange={e => {
                                                            const selected = e.target.value as string[];
                                                            updateMetadataField(field.id, { value: selected.join(', ') });
                                                        }}
                                                        input={<OutlinedInput />}
                                                        displayEmpty
                                                        renderValue={(selected) => {
                                                            const validSelected = (selected as string[]).filter(v => FILE_FORMAT_OPTIONS.includes(v));
                                                            if (validSelected.length === 0) {
                                                                return <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>Select formats…</Typography>;
                                                            }
                                                            return (
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                                                                    {validSelected.map(val => (
                                                                        <Chip key={val} label={val} size="small" sx={{ fontSize: '10px', height: 18 }} />
                                                                    ))}
                                                                </Box>
                                                            );
                                                        }}
                                                        sx={{ width: '100%', minHeight: 26, fontSize: '11px', '& .MuiSelect-select': { py: '2px', px: '6px' } }}
                                                    >
                                                        {FILE_FORMAT_GROUPS.flatMap(group => [
                                                            <ListSubheader key={`header-${group.label}`} sx={{ fontSize: '10px', fontWeight: 700, lineHeight: '22px', color: '#111827', textTransform: 'uppercase', letterSpacing: '0.06em', backgroundColor: '#e5e7eb', textAlign: 'center' }}>
                                                                {group.label}
                                                            </ListSubheader>,
                                                            ...group.formats.map(opt => {
                                                                const meta = FILE_FORMAT_META[opt] ?? { iconBg: '#6b7280', ext: `.${opt.toLowerCase()}` };
                                                                return (
                                                                    <MenuItem key={opt} value={opt} sx={{ px: 1.5, py: 0.4 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                                            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#111827', flex: 1, fontFamily: 'Montserrat' }}>
                                                                                {opt}
                                                                            </Typography>
                                                                            <Box sx={{ backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '4px', px: 0.5, py: '1px', flexShrink: 0 }}>
                                                                                <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: 'monospace', lineHeight: 1.4 }}>
                                                                                    {meta.ext}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </MenuItem>
                                                                );
                                                            }),
                                                        ])}
                                                    </Select>
                                                ) : isLocation ? (
                                                    <Select
                                                        multiple
                                                        size="small"
                                                            disabled={!field.enabled}
                                                            value={selectedCountries}
                                                            onChange={e => {
                                                                const selected = e.target.value as string[];
                                                                updateMetadataField(field.id, { value: selected.join(', ') });
                                                            }}
                                                            input={<OutlinedInput />}
                                                            displayEmpty
                                                            onClose={() => setLocationSearch('')}
                                                            renderValue={() => {
                                                                if (selectedCountries.length === 0) {
                                                                    return <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>Select countries…</Typography>;
                                                                }
                                                                return (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, alignItems: 'center' }}>
                                                                        {visibleChips.map(val => (
                                                                            <Chip
                                                                                key={val}
                                                                                label={val}
                                                                                size="small"
                                                                                onDelete={e => {
                                                                                    e.stopPropagation();
                                                                                    const next = selectedCountries.filter(c => c !== val);
                                                                                    updateMetadataField(field.id, { value: next.join(', ') });
                                                                                }}
                                                                                onMouseDown={e => e.stopPropagation()}
                                                                                sx={{
                                                                                    fontSize: '10px', height: 20, maxWidth: 90,
                                                                                    backgroundColor: '#f3f4f6', color: '#111827',
                                                                                    border: '1px solid #d1d5db',
                                                                                    '& .MuiChip-label': { px: '6px' },
                                                                                    '& .MuiChip-deleteIcon': { fontSize: 12, color: '#9ca3af', '&:hover': { color: '#111827' } },
                                                                                }}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                );
                                                            }}
                                                            sx={{ width: '100%', minHeight: 28, fontSize: '11px', '& .MuiSelect-select': { py: '3px', px: '8px' } }}
                                                            MenuProps={{
                                                                autoFocus: false,
                                                                PaperProps: {
                                                                    sx: {
                                                                        maxHeight: 340,
                                                                        width: 260,
                                                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                                        border: '1px solid #e5e7eb',
                                                                        borderRadius: 2,
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            <ListSubheader sx={{ px: 1, pt: 1, pb: 0.5, backgroundColor: '#fff', position: 'sticky', top: 0, zIndex: 2, lineHeight: 'normal' }}>
                                                                <TextField
                                                                    size="small"
                                                                    autoFocus
                                                                    fullWidth
                                                                    placeholder="Search countries…"
                                                                    value={locationSearch}
                                                                    onChange={e => setLocationSearch(e.target.value)}
                                                                    onKeyDown={e => e.stopPropagation()}
                                                                    sx={{ '& .MuiInputBase-input': { fontSize: '11px', py: '5px' }, '& .MuiInputBase-root': { height: 30 } }}
                                                                />
                                                            </ListSubheader>
                                                            {Object.keys(locationGrouped).length === 0 && (
                                                                <MenuItem disabled sx={{ fontSize: '11px', color: '#9ca3af', justifyContent: 'center', py: 2, minHeight: 'unset' }}>
                                                                    No countries found
                                                                </MenuItem>
                                                            )}
                                                            {Object.keys(locationGrouped).sort().flatMap(letter => [
                                                                <ListSubheader key={`letter-${letter}`} sx={{ fontSize: '9px', fontWeight: 700, lineHeight: '18px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', backgroundColor: '#f9fafb', px: 1.5, borderTop: '1px solid #f3f4f6' }}>
                                                                    {letter}
                                                                </ListSubheader>,
                                                                ...locationGrouped[letter].map(country => (
                                                                    <MenuItem key={country} value={country} sx={{ fontSize: '11px', py: 0.4, px: 1.5, minHeight: 'unset', '&.Mui-selected': { backgroundColor: '#eff6ff' }, '&.Mui-selected:hover': { backgroundColor: '#dbeafe' } }}>
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={selectedCountries.includes(country)}
                                                                            sx={{ p: 0, mr: 0.75, color: '#93c5fd', '&.Mui-checked': { color: '#3b82f6' } }}
                                                                        />
                                                                        <Typography sx={{ fontSize: '11px', color: '#111827' }}>{country}</Typography>
                                                                    </MenuItem>
                                                                )),
                                                            ])}
                                                        </Select>
                                                ) : field.valueType === 'days_select' ? (
                                                    <Select
                                                        size="small"
                                                        value={field.value}
                                                        disabled={!field.enabled}
                                                        displayEmpty
                                                        onChange={e => updateMetadataField(field.id, { value: e.target.value })}
                                                        sx={{ width: '100%', height: 26, fontSize: '11px', '& .MuiSelect-select': { py: '2px', px: '6px' } }}
                                                    >
                                                        {CAPTURE_DATE_OPTIONS.map(opt => (
                                                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '11px' }}>{opt.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                ) : isDeviceMake ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                        <Select
                                                            multiple
                                                            size="small"
                                                            disabled={!field.enabled}
                                                            value={splitMultiselectValue(field.value)}
                                                            onChange={e => {
                                                                const selectedMakes = e.target.value as string[];
                                                                updateMetadataField(field.id, { value: joinMultiselectValue(selectedMakes) });
                                                            }}
                                                            input={<OutlinedInput />}
                                                            displayEmpty
                                                            renderValue={(selected) => {
                                                                const selectedValues = selected as string[];
                                                                if (selectedValues.length === 0) {
                                                                    return <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>Select makes...</Typography>;
                                                                }
                                                                return (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                                                                        {selectedValues.map(makeId => {
                                                                            const makeOpt = deviceMakeOptions.find(m => m.id === makeId);
                                                                            return <Chip key={makeId} label={makeOpt?.name ?? makeId} size="small" sx={{ fontSize: '10px', height: 18 }} />;
                                                                        })}
                                                                    </Box>
                                                                );
                                                            }}
                                                            sx={{ width: '100%', minHeight: 26, fontSize: '11px', '& .MuiSelect-select': { py: '2px', px: '6px' } }}
                                                        >
                                                            {deviceMakeOptions.map(make => (
                                                                <MenuItem key={make.id} value={make.id} sx={{ px: 1.5, py: 0.4, fontSize: '11px' }}>
                                                                    {make.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {metadataErrors[field.id] && (
                                                            <Typography sx={{ fontSize: '10px', color: '#ef4444' }}>
                                                                {metadataErrors[field.id]}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ) : isDeviceModel ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                        {(() => {
                                                            const normalizedDeviceModelSearch = deviceModelSearch.trim().toLowerCase();
                                                            const filteredDeviceModelOptions = normalizedDeviceModelSearch
                                                                ? deviceModelOptions.filter(option => option.name.toLowerCase().includes(normalizedDeviceModelSearch))
                                                                : deviceModelOptions;
                                                            const hasFilteredDeviceModels = filteredDeviceModelOptions.length > 0;

                                                            return (
                                                        <Select
                                                            multiple
                                                            size="small"
                                                            disabled={!field.enabled || selectedDeviceMakes.length === 0}
                                                            value={selectedDeviceModels.filter(modelId => deviceModelOptions.some(option => option.id === modelId))}
                                                            onChange={e => {
                                                                const selectedModels = e.target.value as string[];
                                                                updateMetadataField(field.id, { value: joinMultiselectValue(selectedModels) });
                                                            }}
                                                            input={<OutlinedInput />}
                                                            displayEmpty
                                                            onClose={() => setDeviceModelSearch('')}
                                                            renderValue={(selected) => {
                                                                const selectedValues = (selected as string[]).filter(modelId =>
                                                                    deviceModelOptions.some(option => option.id === modelId),
                                                                );
                                                                if (selectedValues.length === 0) {
                                                                    return (
                                                                        <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
                                                                            {selectedDeviceMakes.length === 0 ? 'Select Device Make first' : 'Select models...'}
                                                                        </Typography>
                                                                    );
                                                                }
                                                                return (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                                                                        {selectedValues.map(modelId => {
                                                                            const modelOption = deviceModelOptions.find(option => option.id === modelId);
                                                                            return (
                                                                                <Chip
                                                                                    key={modelId}
                                                                                    label={modelOption?.name ?? modelId}
                                                                                    size="small"
                                                                                    sx={{ fontSize: '10px', height: 18 }}
                                                                                />
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                );
                                                            }}
                                                            sx={{ width: '100%', minHeight: 26, fontSize: '11px', '& .MuiSelect-select': { py: '2px', px: '6px' } }}
                                                            MenuProps={{
                                                                autoFocus: false,
                                                                PaperProps: {
                                                                    sx: {
                                                                        maxHeight: 340,
                                                                        width: 280,
                                                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                                        border: '1px solid #e5e7eb',
                                                                        borderRadius: 2,
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            {selectedDeviceMakes.length > 0 && (
                                                                <ListSubheader sx={{ px: 1, pt: 1, pb: 0.5, backgroundColor: '#fff', position: 'sticky', top: 0, zIndex: 2, lineHeight: 'normal' }}>
                                                                    <TextField
                                                                        size="small"
                                                                        autoFocus
                                                                        fullWidth
                                                                        placeholder="Search models..."
                                                                        value={deviceModelSearch}
                                                                        onChange={e => setDeviceModelSearch(e.target.value)}
                                                                        onKeyDown={e => e.stopPropagation()}
                                                                        sx={{ '& .MuiInputBase-input': { fontSize: '11px', py: '5px' }, '& .MuiInputBase-root': { height: 30 } }}
                                                                    />
                                                                </ListSubheader>
                                                            )}
                                                            {selectedDeviceMakes.length === 0 && (
                                                                <MenuItem disabled sx={{ fontSize: '11px', color: '#9ca3af', justifyContent: 'center', py: 2, minHeight: 'unset' }}>
                                                                    Select Device Make first
                                                                </MenuItem>
                                                            )}
                                                            {selectedDeviceMakes.length > 0 && !hasFilteredDeviceModels && (
                                                                <MenuItem disabled sx={{ fontSize: '11px', color: '#9ca3af', justifyContent: 'center', py: 2, minHeight: 'unset' }}>
                                                                    No models found
                                                                </MenuItem>
                                                            )}
                                                            {selectedDeviceMakes.map(makeId => {
                                                                const modelsForMake = filteredDeviceModelOptions.filter(option => option.make === makeId);
                                                                if (modelsForMake.length === 0) {
                                                                    return [];
                                                                }

                                                                return [
                                                                    <ListSubheader
                                                                        key={`header-${makeId}`}
                                                                        sx={{ fontSize: '10px', fontWeight: 700, lineHeight: '22px', color: '#111827', textTransform: 'uppercase', letterSpacing: '0.06em', backgroundColor: '#e5e7eb', textAlign: 'center' }}
                                                                    >
                                                                        {makeId}
                                                                    </ListSubheader>,
                                                                    ...modelsForMake.map(option => (
                                                                        <MenuItem key={option.id} value={option.id} sx={{ px: 1.5, py: 0.4 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#111827', flex: 1, fontFamily: 'Montserrat' }}>
                                                                                    {option.name}
                                                                                </Typography>
                                                                            </Box>
                                                                        </MenuItem>
                                                                    )),
                                                                ];
                                                            })}
                                                        </Select>
                                                            );
                                                        })()}
                                                        {metadataErrors[field.id] && (
                                                            <Typography sx={{ fontSize: '10px', color: '#ef4444' }}>
                                                                {metadataErrors[field.id]}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {field.operatorOptions.length > 0 && (
                                                            <Select
                                                                size="small"
                                                                value={field.operator}
                                                                disabled={!field.enabled}
                                                                onChange={e => updateMetadataField(field.id, { operator: e.target.value })}
                                                                sx={{ fontSize: '11px', width: 80, height: 26, '& .MuiSelect-select': { py: '2px', px: '6px' }, flexShrink: 0 }}
                                                            >
                                                                {field.operatorOptions.map(op => (
                                                                    <MenuItem key={op} value={op} sx={{ fontSize: '11px' }}>{op}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        )}
                                                        <TextField
                                                            size="small"
                                                            disabled={!field.enabled}
                                                            value={field.value}

                                                            onChange={e => updateMetadataField(field.id, { value: e.target.value })}
                                                            sx={{
                                                                flex: 1,
                                                                '& .MuiInputBase-input': { fontSize: '11px', py: '2px', px: '6px' },
                                                                '& .MuiInputBase-root': { height: 26 },
                                                            }}
                                                        />
                                                        </Box>
                                                        {metadataErrors[field.id] && (
                                                            <Typography sx={{ fontSize: '10px', color: '#ef4444' }}>
                                                                {metadataErrors[field.id]}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>

                                            <TableCell sx={{ pl: 1 }}>
                                                {isFileFormat ? null : (
                                                    <Switch
                                                        size="small"
                                                        checked={field.enabled}
                                                        disabled={isDeviceModelToggleDisabled}
                                                        onChange={e => updateMetadataField(field.id, { enabled: e.target.checked })}
                                                        sx={{ m: '4px', '& .MuiSwitch-switchBase': { p: '4px' } }}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper sx={sectionPaperSx} elevation={0}>
                            <Typography sx={sectionTitleSx}>Custom Prompt AI Validation</Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, p: 1.25, backgroundColor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
                                <Box>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>Enable Prompt Analysis</Typography>
                                    <Typography sx={{ fontSize: '10px', color: '#9ca3af', mt: 0.25 }}>Run a custom AI prompt against each submission</Typography>
                                </Box>
                                <Switch
                                    size="small"
                                    checked={promptEnabled}
                                    onChange={e => setPromptEnabled(e.target.checked)}
                                />
                            </Box>

                            {promptEnabled && (
                                <>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1.5 }}>
                                        <Box sx={{ position: 'relative' }}>
                                            <Box
                                                ref={editorRef}
                                                contentEditable
                                                suppressContentEditableWarning
                                                onInput={handleEditorInput}
                                                onKeyDown={e => {
                                                    if (e.key === 'Escape') setShowMentionDropdown(false);
                                                }}
                                                sx={{
                                                    minHeight: '112px',
                                                    padding: '12px 14px',
                                                    fontSize: '12px',
                                                    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                                    lineHeight: '1.5',
                                                    backgroundColor: '#f9fafb',
                                                    color: '#111827',
                                                    border: '1px solid rgba(0,0,0,0.23)',
                                                    borderRadius: '6px',
                                                    outline: 'none',
                                                    wordBreak: 'break-word',
                                                    whiteSpace: 'pre-wrap',
                                                    overflowY: 'auto',
                                                    '&:focus': {
                                                        border: '2px solid #1976d2',
                                                        padding: '11px 13px',
                                                    },
                                                    '&:hover:not(:focus)': { border: '1px solid rgba(0,0,0,0.87)' },
                                                }}
                                            />
                                            {!promptText && (
                                                <Typography
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '12px',
                                                        left: '14px',
                                                        fontSize: '12px',
                                                        fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                                        color: '#9ca3af',
                                                        pointerEvents: 'none',
                                                        userSelect: 'none',
                                                    }}
                                                >
                                                    Enter custom AI prompt… type @ to reference a config identifier.
                                                </Typography>
                                            )}
                                            {showMentionDropdown && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: 0,
                                                        right: 0,
                                                        zIndex: 9999,
                                                        backgroundColor: '#ffffff',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: 1.5,
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                                        maxHeight: 180,
                                                        overflowY: 'auto',
                                                        mt: '2px',
                                                    }}
                                                >
                                                    {filteredIdentifiers.length > 0 ? (
                                                        filteredIdentifiers.map(item => (
                                                            <Box
                                                                key={item.id}
                                                                onMouseDown={e => {
                                                                    e.preventDefault();
                                                                    handleMentionSelect(item.identifier);
                                                                }}
                                                                sx={{
                                                                    px: 1.5,
                                                                    py: 0.75,
                                                                    fontSize: '12px',
                                                                    fontFamily: 'Montserrat',
                                                                    color: '#374151',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    '&:hover': { backgroundColor: '#eff6ff', color: '#3D85C6' },
                                                                }}
                                                            >
                                                                <span>{item.identifier}</span>
                                                                <Tooltip
                                                                    title={
                                                                        <Box sx={{ p: 0.5 }}>
                                                                            {item.value?.title !== undefined && (
                                                                                <Typography sx={{ fontSize: '11px' }}>{String(item.value.title)}</Typography>
                                                                            )}
                                                                        </Box>
                                                                    }
                                                                    placement="left"
                                                                    arrow
                                                                    slotProps={{ popper: { sx: { zIndex: 10000 } } }}
                                                                >
                                                                    <IconButton
                                                                        size="small"
                                                                        onMouseDown={e => e.stopPropagation()}
                                                                        sx={{
                                                                            p: 0.25,
                                                                            ml: 0.5,
                                                                            color: '#9ca3af',
                                                                            '&:hover': { color: '#3D85C6', backgroundColor: 'transparent' },
                                                                        }}
                                                                    >
                                                                        <InfoOutlined sx={{ fontSize: 14 }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        ))
                                                    ) : (
                                                        <Box sx={{ px: 1.5, py: 1, fontSize: '12px', color: '#9ca3af' }}>
                                                            No matching identifiers
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                        <Tooltip
                                            title={
                                                <Box sx={{ p: 0.5 }}>
                                                    <Typography sx={{ fontSize: '11px', fontWeight: 700, mb: 0.5 }}>
                                                        Hint: Use @ format to find the config identifier 
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '11px' }}>
                                                        e.g. "Is this @URL valid?"
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '11px' }}>
                                                        e.g. "Verify @field contains correct data."
                                                    </Typography>
                                                </Box>
                                            }
                                            placement="right"
                                            arrow
                                            open={showHint}
                                            onClose={() => setShowHint(false)}
                                            disableFocusListener
                                            disableHoverListener
                                            disableTouchListener
                                        >
                                            <Box
                                                component="button"
                                                onClick={() => setShowHint(v => !v)}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '5px',
                                                    border: `1px solid ${showHint ? '#d97706' : '#fcd34d'}`,
                                                    borderRadius: '20px',
                                                    px: 1.25,
                                                    py: 0.4,
                                                    backgroundColor: showHint ? '#fef3c7' : '#fffbeb',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.18s ease',
                                                    outline: 'none',
                                                    '&:hover': {
                                                        backgroundColor: '#fef3c7',
                                                        borderColor: '#d97706',
                                                    },
                                                }}
                                            >
                                                <LightbulbOutlined sx={{ fontSize: 13, color: '#d97706' }} />
                                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#b45309', lineHeight: 1 }}>
                                                    Hint
                                                </Typography>
                                            </Box>
                                        </Tooltip>

                                        {hasDynamicValueWarning && (
                                            <Tooltip
                                                title="Prompt may contain Dynamic Value so it will incur extra cost"
                                                placement="right"
                                                arrow
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        border: '1px solid #fca5a5',
                                                        borderRadius: '20px',
                                                        px: 1.25,
                                                        py: 0.4,
                                                        backgroundColor: '#fef2f2',
                                                        cursor: 'default',
                                                    }}
                                                >
                                                    <WarningAmberOutlined sx={{ fontSize: 13, color: '#dc2626' }} />
                                                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#b91c1c', lineHeight: 1 }}>
                                                        Extra Cost
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        )}
                                        </Box>
                                    </Box>

                                    <Divider sx={{ mb: 1.5, mt: 1.5 }} />

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography sx={{ fontSize: '10px', color: '#6b7280', whiteSpace: 'nowrap', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Schedule
                                        </Typography>
                                        <Select
                                            size="small"
                                            fullWidth
                                            value={promptSchedule}
                                            onChange={e => setPromptSchedule(e.target.value as Schedule)}
                                            sx={{
                                                fontSize: '12px',
                                                height: 32,
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                                            }}
                                        >
                                            <MenuItem value="on_image_upload" sx={{ fontSize: '12px' }}>On Upload</MenuItem>
                                            <MenuItem value="on_task_submission" sx={{ fontSize: '12px' }}>On Task Submission</MenuItem>
                                        </Select>
                                    </Box>

                                </>
                            )}
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper sx={sectionPaperSx} elevation={0}>
                            <Typography sx={sectionTitleSx}>Additional AI Validation</Typography>
                            <Typography sx={sectionSubtitleSx}>Configure extra validation checks.</Typography>

                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
                                {[...additionalValidations]
                                    .sort((a, b) => {
                                        const order = ['blur', 'pii', 'duplication'];
                                        return order.indexOf(a.id) - order.indexOf(b.id);
                                    })
                                    .map(v => (
                                    <Box
                                        key={v.id}
                                        sx={{
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 2,
                                            p: 1.5,
                                            backgroundColor: '#ffffff',
                                            transition: 'all 0.2s',
                                            ...(v.id === 'duplication' && { gridColumn: '1 / -1' }),
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: v.enabled ? '#1e40af' : '#6b7280' }}>
                                                {v.label}
                                            </Typography>
                                            <Switch
                                                size="small"
                                                checked={v.enabled}
                                                onChange={e => updateAdditionalValidation(v.id, { enabled: e.target.checked })}
                                                sx={{ ml: 1 }}
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Typography sx={{ fontSize: '10px', color: '#6b7280', whiteSpace: 'nowrap', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 60 }}>
                                                Schedule
                                            </Typography>
                                            <Select
                                                size="small"
                                                fullWidth
                                                disabled={!v.enabled}
                                                value={v.schedule}
                                                onChange={e =>
                                                    updateAdditionalValidation(v.id, { schedule: e.target.value as Schedule })
                                                }
                                                sx={{
                                                    fontSize: '11px',
                                                    height: 28,
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: 1,
                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                                                }}
                                            >
                                                <MenuItem value="on_image_upload" sx={{ fontSize: '11px' }}>On Upload</MenuItem>
                                                <MenuItem value="on_task_submission" sx={{ fontSize: '11px' }}>On Task Submission</MenuItem>
                                            </Select>
                                        </Box>

                                        {v.id === 'duplication' && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, mt: 0.5 }}>
                                                <Typography sx={{ fontSize: '10px', color: '#6b7280', whiteSpace: 'nowrap', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 60 }}>
                                                    Type
                                                </Typography>
                                                <Select
                                                    size="small"
                                                    fullWidth
                                                    disabled={!v.enabled}
                                                    value={v.duplicationType ?? 'custom_literal_path'}
                                                    onChange={e =>
                                                        updateAdditionalValidation(v.id, { duplicationType: e.target.value as DuplicationType })
                                                    }
                                                    sx={{
                                                        fontSize: '11px',
                                                        height: 28,
                                                        backgroundColor: '#ffffff',
                                                        borderRadius: 1,
                                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                                                    }}
                                                >
                                                    <MenuItem value="custom_literal_path" sx={{ fontSize: '11px' }}>Custom Literal Path</MenuItem>
                                                    <MenuItem value="custom_template_path" sx={{ fontSize: '11px' }}>Custom Template Path</MenuItem>
                                                </Select>
                                            </Box>
                                        )}

                                        {v.id === 'duplication' && v.duplicationType === 'custom_literal_path' && (
                                            <Box sx={{ mt: 1 }}>
                                                {(v.literalPaths ?? ['']).map((path, pathIdx) => (
                                                    <Box key={pathIdx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            disabled={!v.enabled}
                                                            value={path}
                                                            onChange={e => {
                                                                const paths = [...(v.literalPaths ?? [''])];
                                                                paths[pathIdx] = e.target.value;
                                                                updateAdditionalValidation(v.id, { literalPaths: paths });
                                                            }}
                                                            placeholder="folder/sub_folder/images/image.jpg"
                                                            sx={{
                                                                '& .MuiInputBase-input': { fontSize: '11px' },
                                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: (v.enabled && path.trim() === '') ? '#ef4444' : '#e5e7eb' },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: (v.enabled && path.trim() === '') ? '#dc2626' : '#3D85C6' },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                                                            }}
                                                        />
                                                        {(v.literalPaths ?? ['']).length > 1 && (
                                                            <IconButton
                                                                size="small"
                                                                disabled={!v.enabled}
                                                                onClick={() => {
                                                                    const paths = (v.literalPaths ?? ['']).filter((_, i) => i !== pathIdx);
                                                                    updateAdditionalValidation(v.id, { literalPaths: paths });
                                                                }}
                                                                sx={{ mt: 0.25, color: '#ef4444', p: 0.5, flexShrink: 0 }}
                                                            >
                                                                <CloseOutlined sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}
                                                <Box
                                                    component="button"
                                                    disabled={!v.enabled}
                                                    onClick={() => {
                                                        const paths = [...(v.literalPaths ?? ['']), ''];
                                                        updateAdditionalValidation(v.id, { literalPaths: paths });
                                                    }}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5,
                                                        background: 'none', border: 'none', cursor: 'pointer', p: 0,
                                                        color: '#3D85C6', fontSize: '11px', fontWeight: 600,
                                                        '&:disabled': { color: '#9ca3af', cursor: 'not-allowed' },
                                                        '&:hover:not(:disabled)': { color: '#2563eb' },
                                                    }}
                                                >
                                                    <AddCircleOutlineOutlined sx={{ fontSize: 14 }} />
                                                    Add Path
                                                </Box>
                                                {v.enabled && !(v.literalPaths ?? []).some(p => p.trim() !== '') && (
                                                    <Typography sx={{ fontSize: '10px', color: '#ef4444', mt: 0.5 }}>
                                                        At least one path is required when Duplication Check is enabled.
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}

                                        {v.id === 'duplication' && v.duplicationType === 'custom_template_path' && (
                                            <Box sx={{ mt: 1 }}>
                                                {!useCustomStorage && (
                                                    <Box sx={{
                                                        display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.75,
                                                        p: 0.75, backgroundColor: '#fef9c3', borderRadius: 1, border: '1px solid #fde047',
                                                    }}>
                                                        <WarningAmberOutlined sx={{ fontSize: 13, color: '#b45309', mt: '1px', flexShrink: 0 }} />
                                                        <Typography sx={{ fontSize: '10px', color: '#92400e', lineHeight: 1.4 }}>
                                                            Custom Storage Provider not selected. Data will be saved under <strong>project_id</strong>.
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {(v.templatePaths ?? ['']).map((path, pathIdx) => (
                                                    <Box key={pathIdx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                                                        <TemplatePathField
                                                            value={path}
                                                            onChange={val => {
                                                                const paths = [...(v.templatePaths ?? [''])];
                                                                paths[pathIdx] = val;
                                                                updateAdditionalValidation(v.id, { templatePaths: paths });
                                                            }}
                                                            disabled={!v.enabled}
                                                            endpointUrlInput={endpointUrlInput}
                                                            useCustomStorage={useCustomStorage}
                                                            showWarning={false}
                                                            isLast={pathIdx === (v.templatePaths ?? ['']).length - 1}
                                                        />
                                                        {(v.templatePaths ?? ['']).length > 1 && (
                                                            <IconButton
                                                                size="small"
                                                                disabled={!v.enabled}
                                                                onClick={() => {
                                                                    const paths = (v.templatePaths ?? ['']).filter((_, i) => i !== pathIdx);
                                                                    updateAdditionalValidation(v.id, { templatePaths: paths });
                                                                }}
                                                                sx={{ mt: 0.25, color: '#ef4444', p: 0.5, flexShrink: 0 }}
                                                            >
                                                                <CloseOutlined sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}
                                                <Box
                                                    component="button"
                                                    disabled={!v.enabled}
                                                    onClick={() => {
                                                        const paths = [...(v.templatePaths ?? ['']), ''];
                                                        updateAdditionalValidation(v.id, { templatePaths: paths });
                                                    }}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5,
                                                        background: 'none', border: 'none', cursor: 'pointer', p: 0,
                                                        color: '#3D85C6', fontSize: '11px', fontWeight: 600,
                                                        '&:disabled': { color: '#9ca3af', cursor: 'not-allowed' },
                                                        '&:hover:not(:disabled)': { color: '#2563eb' },
                                                    }}
                                                >
                                                    <AddCircleOutlineOutlined sx={{ fontSize: 14 }} />
                                                    Add Path
                                                </Box>
                                                {v.enabled && !(v.templatePaths ?? []).some(p => p.trim() !== '') && (
                                                    <Typography sx={{ fontSize: '10px', color: '#ef4444', mt: 0.5 }}>
                                                        At least one path is required when Duplication Check is enabled.
                                                    </Typography>
                                                )}

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                    <Typography sx={{ fontSize: '10px', color: '#6b7280', whiteSpace: 'nowrap', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>
                                                        Resolution Mode
                                                    </Typography>
                                                    <Select
                                                        size="small"
                                                        fullWidth
                                                        disabled={!v.enabled}
                                                        value={v.resolutionMode ?? 'current_path_only'}
                                                        onChange={e => updateAdditionalValidation(v.id, { resolutionMode: e.target.value as ResolutionMode })}
                                                        sx={{
                                                            fontSize: '11px',
                                                            height: 28,
                                                            backgroundColor: '#ffffff',
                                                            borderRadius: 1,
                                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                                                        }}
                                                    >
                                                        <MenuItem value="current_path_only" sx={{ fontSize: '11px' }}>Current Path Only</MenuItem>
                                                        <MenuItem value="all_resolved_paths" sx={{ fontSize: '11px' }}>All Resolved Paths</MenuItem>
                                                    </Select>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper sx={sectionPaperSx} elevation={0}>
                            <Typography sx={sectionTitleSx}>AI Validation Rules</Typography>
                            <Typography sx={sectionSubtitleSx}>Define AI rules and confidence thresholds.</Typography>

                            <Grid container spacing={2}>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ border: '1px solid #e5e7eb', borderTop: '3px solid #f59e0b', borderRadius: 2, p: 1.5, height: '100%' }}>
                                        <Typography sx={{ fontSize: '10px', color: '#b45309', mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Partial Pass if confidence between
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TextField
                                                size="small"
                                                value={aiRules.partialMin}
                                                onChange={handleNumericChange('partialMin')}
                                                inputProps={{ inputMode: 'numeric', maxLength: 3 }}
                                                sx={{
                                                    width: 72,
                                                    '& .MuiInputBase-input': { fontSize: '13px', textAlign: 'center' },
                                                    '& .MuiInputBase-root': { height: 34 },
                                                }}
                                            />
                                            <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>–</Typography>
                                            <TextField
                                                size="small"
                                                value={aiRules.partialMax}
                                                onChange={handleNumericChange('partialMax')}
                                                inputProps={{ inputMode: 'numeric', maxLength: 3 }}
                                                sx={{
                                                    width: 72,
                                                    '& .MuiInputBase-input': { fontSize: '13px', textAlign: 'center' },
                                                    '& .MuiInputBase-root': { height: 34 },
                                                }}
                                            />
                                            <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>%</Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ border: '1px solid #e5e7eb', borderTop: '3px solid #22c55e', borderRadius: 2, p: 1.5, height: '100%' }}>
                                        <Typography sx={{ fontSize: '10px', color: '#16a34a', mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Full Pass if confidence &gt;
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TextField
                                                size="small"
                                                value={aiRules.fullPassThreshold}
                                                onChange={handleNumericChange('fullPassThreshold')}
                                                inputProps={{ inputMode: 'numeric', maxLength: 3 }}
                                                sx={{
                                                    width: 72,
                                                    '& .MuiInputBase-input': { fontSize: '13px', textAlign: 'center' },
                                                    '& .MuiInputBase-root': { height: 34 },
                                                }}
                                            />
                                            <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>%</Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ border: '1px solid #e5e7eb', borderTop: '3px solid #3D85C6', borderRadius: 2, p: 1.5 }}>
                                        <Typography sx={{ fontSize: '10px', color: '#3D85C6', mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            On Full Pass
                                        </Typography>
                                        <Select
                                            size="small"
                                            fullWidth
                                            value={aiRules.fullPassAction}
                                            onChange={e => handleAIRuleChange('fullPassAction', e.target.value)}
                                            sx={{ fontSize: '12px', height: 36 }}
                                        >
                                            <MenuItem value="send_to_next" sx={{ fontSize: '12px' }}>Send to next stage</MenuItem>
                                            <MenuItem value="mark_accepted" sx={{ fontSize: '12px' }}>Mark as Quality Accepted</MenuItem>
                                        </Select>
                                    </Box>
                                </Grid>

                            </Grid>
                        </Paper>
                    </Grid>

                </Grid>

            </Box>
        </>
    );
};

export default AdvancedAiSettings;
